import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapImage from '@tiptap/extension-image';
import Heading from '@tiptap/extension-heading';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from 'react-tooltip';
import {
  Bold, Italic, Link as LinkIcon, List, ListOrdered, User, Calendar,
  Clock, AlertTriangle, CheckCircle2, MessageSquare, Send, Users,
  Edit2, Download, FileText, Image, FileArchive, FileCode, X,
  Upload, Loader2, Flag, Hash, Trash2, Maximize2, Minimize2, ChevronDown, ChevronUp,
  Heading1, Heading2, Heading3, Type, Palette, Eraser, CheckSquare, ChevronRight, FolderOpen,
  ExternalLink, Paperclip, BellRing, Languages, Link2, MessageSquareShare, CalendarPlus
} from 'lucide-react';
import { useUserStore } from '../../lib/stores/userStore';
import { useProjectStore } from '../../lib/stores/projectStore';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import type { Task, TaskComment, TaskCommentAttachment, TaskAttachment, RecurrenceType, RecurrenceUnit, RecurrenceEndType } from '../../lib/types';
import { TaskChecklist } from './TaskChecklist';
import { TaskReminderField, TaskLocationField } from './TaskReminderLocationFields';
import { TaskCustomFields } from './TaskCustomFields';
import { FluTaskOutcome } from './FluTaskOutcome';
import type { TaskCustomFieldDef } from '../../lib/types';
import { SignedUrl } from '../shared/SignedUrl';
import { TaskTimeTracker } from './TaskTimeTracker';
import { RecurrenceSelector } from './RecurrenceSelector';
import { CommentEditor } from '../ui/CommentEditor';
import { CommentAttachments } from './CommentAttachments';
import { MentionNotificationService } from '../../lib/services/mentionNotificationService';
import { notificationTriggerService } from '../../lib/events/notificationTriggerService';
import { useDebouncedCallback } from '../../lib/hooks/useDebounce';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP } from '../../lib/hooks/useOrgAiEnabled';
import { LightModeWrapper } from '../ui/LightModeWrapper';
import { supabase } from '../../lib/supabase';
import { ShareToChatModal, ShareEntityType } from '../team-chat/ShareToChatModal';
import { AUDIO_TRANSCRIPTION_LEGACY_ENABLED } from '@/lib/featureFlags';
import { CalendarModal } from '../calendar/CalendarModal';
import { useCalendarStore } from '../../lib/stores/calendarStore';

interface TaskDrawerProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (taskId: string, data: Partial<Task>) => Promise<void>;
  onDelete: (taskId: string) => Promise<void>;
  onAddComment: (taskId: string, content: string) => Promise<void>;
  availableSections?: Array<{ id: string; title: string }>;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'todo':
      return 'bg-gray-400';
    case 'in_progress':
      return 'bg-blue-400';
    case 'review':
      return 'bg-yellow-400';
    case 'completed':
      return 'bg-green-400';
    case 'standby':
      return 'bg-purple-400';
    default:
      return 'bg-gray-400';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return 'bg-red-400';
    case 'high':
      return 'bg-orange-400';
    case 'medium':
      return 'bg-yellow-400';
    case 'low':
      return 'bg-green-400';
    default:
      return 'bg-gray-400';
  }
};

// Component for truncating long comments with "Show more/less"
function ExpandableComment({ html, children, maxHeight = 100, fadeClassName }: { html?: string; children?: React.ReactNode; maxHeight?: number; fadeClassName?: string }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > maxHeight);
    }
  }, [html, children, maxHeight]);

  return (
    <div>
      <div
        ref={contentRef}
        className={`relative ${!isExpanded && isOverflowing ? 'overflow-hidden' : ''}`}
        style={!isExpanded && isOverflowing ? { maxHeight: `${maxHeight}px` } : undefined}
      >
        {html ? (
          <div
            className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-p:leading-relaxed prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-strong:font-semibold prose-a:text-blue-600 dark:prose-a:text-blue-400"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : children}
        {!isExpanded && isOverflowing && (
          <div className={`absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t ${fadeClassName || 'from-[#FBFBFA] dark:from-gray-800'} to-transparent pointer-events-none`} />
        )}
      </div>
      {isOverflowing && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[10px] text-[#2383E2] dark:text-blue-400 hover:underline mt-1 flex items-center gap-0.5"
        >
          {isExpanded ? (
            <>Mostra meno <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>Mostra di più <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}
    </div>
  );
}

function parseCollaboratorComment(content: string): { isCollaborator: boolean; name: string; cleanContent: string } {
  if (!content) return { isCollaborator: false, name: '', cleanContent: content };
  const match = content.match(/^\[Collaborator[e]?:\s*(.+?)\]\n?([\s\S]*)$/);
  if (match) {
    return { isCollaborator: true, name: match[1].trim(), cleanContent: match[2] };
  }
  return { isCollaborator: false, name: '', cleanContent: content };
}

function processCommentContent(content: string): string {
  if (!content) return '';

  // Strip collaborator prefix before processing
  const { cleanContent } = parseCollaboratorComment(content);
  const toProcess = cleanContent;

  const isHtml = /<[a-z][\s\S]*>/i.test(toProcess);

  let processed = toProcess
    .replace(/@\[([^\]]+)\]\([^)]+\)/g, '<span class="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1 rounded font-medium">@$1</span>')
    .replace(/(https?:\/\/[^\s<>"']+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:underline break-all">$1</a>');

  if (!isHtml) {
    processed = processed
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  }

  return processed;
}

export function TaskDrawer({ task, isOpen, onClose, onUpdate, onDelete, onAddComment, availableSections = [] }: TaskDrawerProps) {
  const { users, loading: usersLoading, fetchUsers } = useUserStore();
  const {
    tasks,
    projects,
    updateLocalTask,
    fetchTaskDetails,
    uploadTaskAttachment,
    deleteTaskAttachment,
    createTaskChecklistItem,
    updateTaskChecklistItem,
    deleteTaskChecklistItem,
    toggleChecklistItem,
    deleteTaskComment,
    updateTaskComment,
    uploadCommentAttachment,
    deleteCommentAttachment,
    createTaskCommentWithAttachments,
    briefDocuments,
    fetchBriefDocuments,
    projectClientAccesses,
    fetchProjectClientAccesses,
    projectCollaboratorAccesses,
    fetchProjectCollaboratorAccesses,
    assignTaskToClient,
    selectedProject,
    updateProject,
    // Client comments
    taskClientComments,
    taskClientCommentsLoading,
    fetchTaskClientComments,
    replyToClientComment,
    markClientCommentsAsRead,
    addClientCommentLocally,
    transcribeClientComment,
  } = useProjectStore();
  const { toast } = useApp();
  const { user } = useAuth();
  
  // Local state for form inputs
  const [formState, setFormState] = useState({
    title: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    assigned_client_access_id: '',
    start_date: '',
    due_date: '',
    description: '',
    section: '',
    // Campi ricorrenza
    is_recurring: false as boolean,
    recurrence_type: undefined as RecurrenceType | undefined,
    recurrence_interval: undefined as number | undefined,
    recurrence_unit: undefined as RecurrenceUnit | undefined,
    recurrence_end_type: undefined as RecurrenceEndType | undefined,
    recurrence_end_date: undefined as string | undefined,
    recurrence_max_count: undefined as number | undefined,
  });
  const [comment, setComment] = useState('');
  const [commentMentions, setCommentMentions] = useState<string[]>([]);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [pendingCommentFiles, setPendingCommentFiles] = useState<File[]>([]);

  // Dual-zone drag-drop state
  const [dragOverZone, setDragOverZone] = useState<'task' | 'comment' | null>(null);

  // Client comment reply state
  const [clientCommentReply, setClientCommentReply] = useState('');
  const [isSubmittingClientReply, setIsSubmittingClientReply] = useState(false);
  const [notifyClientOnReply, setNotifyClientOnReply] = useState(false);
  const [transcribingCommentIds, setTranscribingCommentIds] = useState<Set<string>>(new Set());
  const [showShareToChat, setShowShareToChat] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string } | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: number }>({});

  const [activeTab, setActiveTab] = useState<'details' | 'activity' | 'files' | 'brief'>('details');

  // Expanded state with localStorage persistence
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('taskDrawer_expanded');
    return saved === 'true';
  });

  // Track Ctrl/Cmd key for link click behavior
  const [isModKeyPressed, setIsModKeyPressed] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) setIsModKeyPressed(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) setIsModKeyPressed(false);
    };
    const handleBlur = () => setIsModKeyPressed(false);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Drawer width state with localStorage persistence
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const saved = localStorage.getItem('taskDrawer_width');
    return saved ? parseInt(saved) : 42; // Default 42% of viewport width
  });

  // Resize state
  const [isResizing, setIsResizing] = useState(false);

  // Window width state for responsive behavior
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Comments panel state for expanded mode
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  // State for hiding/showing system comments
  const [hideSystemComments, setHideSystemComments] = useState(() => {
    const saved = localStorage.getItem('taskDrawer_hideSystemComments');
    return saved === null ? true : saved === 'true'; // Default: nascosti
  });

  // State for editing comments
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [editingCommentMentions, setEditingCommentMentions] = useState<string[]>([]);

  // State for collaborator comment translations (commentId → translated text)
  const [collabTranslations, setCollabTranslations] = useState<Map<string, string>>(new Map());
  const [translatingCommentIds, setTranslatingCommentIds] = useState<Set<string>>(new Set());
  // Interruttore IA dell'azienda: spento = traduzione dei commenti disabilitata.
  const aiEnabled = useOrgAiEnabled();

  // State for checklist expand/collapse
  const [isChecklistExpanded, setIsChecklistExpanded] = useState(false);

  // State for date tab (start or due)
  const [activeDateTab, setActiveDateTab] = useState<'due' | 'start'>('due');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const dateDropdownRef = useRef<HTMLDivElement>(null);

  // State for "Schedula su calendario" (A1): apre CalendarModal (time-grid) pre-compilato
  // e crea un WORK-SLOT (compare nella Pianificazione + sync Google) collegato alla task.
  const createWorkSlot = useCalendarStore(state => state.createWorkSlot);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const SCHEDULE_DEFAULT_DURATION = 60; // minuti

  // State for searchable user dropdown
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const clientCommentsEndRef = useRef<HTMLDivElement>(null);
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const commentFileInputRef = useRef<HTMLInputElement>(null);

  // Sync form state with task prop
  useEffect(() => {
    if (task) {
      setFormState({
        title: task.title,
        status: task.status,
        priority: task.priority,
        assigned_to: task.assigned_to || '',
        assigned_client_access_id: task.assigned_client_access_id || '',
        start_date: task.start_date?.split('T')[0] || '',
        due_date: task.due_date?.split('T')[0] || '',
        description: task.description || '',
        section: task.section || '',
        // Campi ricorrenza
        is_recurring: task.is_recurring || false,
        recurrence_type: task.recurrence_type,
        recurrence_interval: task.recurrence_interval,
        recurrence_unit: task.recurrence_unit,
        recurrence_end_type: task.recurrence_end_type,
        recurrence_end_date: task.recurrence_end_date,
        recurrence_max_count: task.recurrence_max_count,
      });
      setComment('');
      setPreviewFile(null);
      setUploadingFiles({});
    }
  }, [task]);

  // Fetch project brief documents when task has a project
  useEffect(() => {
    if (task?.project_id) {
      fetchBriefDocuments(task.project_id);
    }
  }, [task?.project_id, fetchBriefDocuments]);

  // Fetch client accesses when task has a project
  useEffect(() => {
    if (task?.project_id) {
      fetchProjectClientAccesses(task.project_id);
    }
  }, [task?.project_id, fetchProjectClientAccesses]);

  // Fetch collaborator accesses when task has a project
  useEffect(() => {
    if (task?.project_id) {
      fetchProjectCollaboratorAccesses(task.project_id);
    }
  }, [task?.project_id, fetchProjectCollaboratorAccesses]);

  // Fetch client comments when task is opened
  useEffect(() => {
    if (task?.id) {
      fetchTaskClientComments(task.id);
      // Mark as read when opening
      markClientCommentsAsRead(task.id);
    }
  }, [task?.id, fetchTaskClientComments, markClientCommentsAsRead]);

  // Contenuto di commenti e checklist: la lista ne porta solo i conteggi, qui servono per esteso
  useEffect(() => {
    if (isOpen && task?.id) {
      fetchTaskDetails(task.id);
    }
  }, [isOpen, task?.id, fetchTaskDetails]);

  // Fetch users when drawer opens (needed for mentions in comments)
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Fetch collaborator accesses when drawer opens
      if (task?.project_id) {
        fetchProjectCollaboratorAccesses(task.project_id);
      }
    }
  }, [isOpen, fetchUsers, fetchProjectCollaboratorAccesses, task?.project_id]);

  // Track active task drawer for client comment notification debounce
  useEffect(() => {
    if (isOpen && task) {
      notificationTriggerService.setActiveTaskDrawerTask(task.id);
    } else {
      notificationTriggerService.setActiveTaskDrawerTask(null);
    }
    return () => {
      notificationTriggerService.setActiveTaskDrawerTask(null);
    };
  }, [isOpen, task?.id]);

  // Listen for realtime client comment inserts
  useEffect(() => {
    const handleNewClientComment = (event: CustomEvent) => {
      console.log('[TaskDrawer] 📥 Evento client-comment-realtime-insert ricevuto:', event.detail);
      const { comment } = event.detail;
      // If the comment is for the current task, add it locally (no refetch for smoother UX)
      if (task && comment.task_id === task.id) {
        console.log('[TaskDrawer] ✅ Commento per task corrente, aggiungo localmente');
        addClientCommentLocally({
          id: comment.id,
          task_id: comment.task_id,
          content: comment.content,
          author_type: comment.author_type,
          author_id: comment.author_id,
          author_name: comment.author_name,
          is_read: comment.is_read,
          created_at: comment.created_at
        });
      } else {
        console.log('[TaskDrawer] ⏭️ Commento per altra task, skip. Current:', task?.id, 'Comment task:', comment.task_id);
      }
    };

    window.addEventListener('client-comment-realtime-insert', handleNewClientComment as EventListener);
    return () => {
      window.removeEventListener('client-comment-realtime-insert', handleNewClientComment as EventListener);
    };
  }, [task?.id, addClientCommentLocally]);

  // Auto-scroll client comments to bottom when loaded or updated
  useEffect(() => {
    if (clientCommentsEndRef.current && taskClientComments.length > 0) {
      clientCommentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [taskClientComments]);

  // Sync isExpanded state with localStorage
  useEffect(() => {
    localStorage.setItem('taskDrawer_expanded', isExpanded.toString());
  }, [isExpanded]);

  // Sync drawerWidth state with localStorage
  useEffect(() => {
    localStorage.setItem('taskDrawer_width', drawerWidth.toString());
  }, [drawerWidth]);

  // Sync hideSystemComments state with localStorage
  useEffect(() => {
    localStorage.setItem('taskDrawer_hideSystemComments', hideSystemComments.toString());
  }, [hideSystemComments]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Snap points for drawer width (in percentage)
  const snapPoints = [35, 50, 65, 80];

  // Find nearest snap point
  const findNearestSnapPoint = (width: number) => {
    return snapPoints.reduce((prev, curr) =>
      Math.abs(curr - width) < Math.abs(prev - width) ? curr : prev
    );
  };

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Handle resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isExpanded) {
        const viewportWidth = window.innerWidth;
        const newWidth = ((viewportWidth - e.clientX) / viewportWidth) * 100;
        // Clamp between 25% and 85%
        const clampedWidth = Math.min(Math.max(newWidth, 25), 85);
        setDrawerWidth(clampedWidth);
      }
    };

    const handleMouseUp = () => {
      if (!isExpanded) {
        // Snap to nearest point
        const snappedWidth = findNearestSnapPoint(drawerWidth);
        setDrawerWidth(snappedWidth);
      }
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, drawerWidth, isExpanded]);

  // Close user dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
        setUserSearchQuery('');
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  // Close date dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setIsDateDropdownOpen(false);
      }
    };

    if (isDateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDateDropdownOpen]);

  // Data/durata di pre-compilazione per CalendarModal (slot iniziale).
  // Usa la due_date della task se presente, altrimenti "ora".
  const scheduleInitialDateTime = useMemo<Date>(() => {
    if (formState.due_date) {
      const parsed = new Date(`${formState.due_date}T09:00:00`);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  }, [formState.due_date]);

  // Crea un WORK-SLOT collegato alla task: compare nella Pianificazione, viene
  // sincronizzato sul Google Calendar dell'assegnatario (via edge `create-work-slot`)
  // e la due_date viene allineata dalla sync inversa. Unifica le due schedulazioni.
  // Riceve start/end dallo slot selezionato/trascinato nel CalendarModal.
  const handleScheduleConfirm = useCallback(async (start: Date, end: Date) => {
    if (!task?.id) return;

    // Il work-slot richiede un progetto e un assegnatario.
    if (!task.project_id) {
      toast.error('La task deve appartenere a un progetto per essere schedulata sul calendario');
      return;
    }
    if (!formState.assigned_to) {
      toast.error('Assegna la task a un utente prima di schedularla sul calendario');
      return;
    }

    const assigneeName =
      users.find(u => u.id === formState.assigned_to)?.full_name ||
      users.find(u => u.id === formState.assigned_to)?.email ||
      'assegnatario';

    const projectName = selectedProject?.name || 'Progetto';

    try {
      const result = await createWorkSlot({
        projectId: task.project_id,
        projectName,
        startDate: start,
        endDate: end,
        assignedTo: formState.assigned_to,
        taskIds: [task.id],
        metadata: {
          project_name: projectName,
          notes: formState.title || task.title,
        },
      });

      if (result) {
        toast.success(`Task schedulato sul calendario di ${assigneeName}`);
        // Allinea la due_date della task allo start scelto (la sync edge la imposta già,
        // ma aggiorniamo lo stato locale per riflettere subito il cambiamento in UI).
        const startDay = start.toISOString().split('T')[0];
        if (startDay && startDay !== formState.due_date) {
          handleFieldChange('due_date', startDay);
        }
        setIsScheduleOpen(false);
      } else {
        toast.error('Errore durante la schedulazione della task sul calendario');
      }
    } catch (error) {
      console.error('Error scheduling task on calendar:', error);
      toast.error('Errore durante la schedulazione della task sul calendario');
    }
  }, [
    task,
    formState.assigned_to,
    formState.due_date,
    formState.title,
    selectedProject,
    users,
    createWorkSlot,
    // handleFieldChange è definito più sotto (const non memoizzato): NON metterlo nelle
    // deps o causa TDZ ("Cannot access 'handleFieldChange' before initialization").
    // Viene comunque chiamato a runtime nel corpo, quando è già inizializzato.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    toast,
  ]);

  // Debounced callback for description updates
  const debouncedDescriptionUpdate = useDebouncedCallback(
    async (content: string) => {
      if (!task?.id || !user?.id) return;
      if (content === task.description) return; // Evita salvataggi inutili

      try {
        await onUpdate(task.id, { description: content });
      } catch (error) {
        console.error('Error updating description:', error);
        toast.error('Errore aggiornamento descrizione');
      }
    },
    2000, // 2 secondi di debounce
    []
  );

  // Editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Disable default heading to use custom one
      }),
      Heading.configure({
        levels: [1, 2, 3],
        HTMLAttributes: {
          class: 'font-bold',
        },
      }),
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-700 underline',
          'data-tooltip-id': 'task-editor-link-hint',
          'data-tooltip-content': `${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} + click per aprire`,
        },
      }),
      Placeholder.configure({
        placeholder: 'Aggiungi una descrizione...',
      }),
      TiptapImage.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-2',
        },
      }),
    ],
    content: formState.description,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      // Chiama il debounced callback
      debouncedDescriptionUpdate(content);
    },
    onBlur: ({ editor }) => {
      // Al blur, salva immediatamente se c'è contenuto diverso
      const content = editor.getHTML();
      if (content !== task?.description && task?.id && user?.id) {
        onUpdate(task.id, { description: content }).catch(err => {
          console.error('Error saving description on blur:', err);
        });
      }
    },
    editorProps: {
      handleClick: (view, pos, event) => {
        if ((event.metaKey || event.ctrlKey) && event.target instanceof HTMLAnchorElement) {
          event.preventDefault();
          const href = event.target.getAttribute('href');
          if (href) {
            window.open(href, '_blank', 'noopener,noreferrer');
          }
          return true;
        }
        return false;
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer?.files?.length) {
          const files = Array.from(event.dataTransfer.files);
          const imageFiles = files.filter(file => file.type.startsWith('image/'));

          if (imageFiles.length > 0) {
            event.preventDefault();

            imageFiles.forEach(async (file) => {
              // Upload image using existing attachment upload
              if (task?.id) {
                try {
                  const url = await uploadTaskAttachment(task.id, file);
                  if (url && editor) {
                    // Insert image at drop position
                    const { schema } = view.state;
                    const coordinates = view.posAtCoords({
                      left: event.clientX,
                      top: event.clientY,
                    });

                    if (coordinates) {
                      editor.chain().focus().setImage({ src: url }).run();
                    }
                  }
                } catch (error) {
                  console.error('Error uploading image:', error);
                  toast.error('Impossibile caricare l\'immagine');
                }
              }
            });

            return true;
          }
        }
        return false;
      },
    },
  });

  // Flush pending description save on unmount or task change
  useEffect(() => {
    return () => {
      debouncedDescriptionUpdate.flush();
    };
  }, [task?.id, debouncedDescriptionUpdate]);

  // Sync editor content when task changes
  useEffect(() => {
    if (editor && task?.description !== undefined) {
      const currentContent = editor.getHTML();
      // Aggiorna solo se il contenuto è effettivamente diverso e l'editor non è focused
      if (task.description !== currentContent && !editor.isFocused) {
        editor.commands.setContent(task.description || '');
      }
    }
  }, [editor, task?.description, task?.id]);

  // Focus title input when editing
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // File upload configuration - Task attachments
  const handleTaskFileDrop = useCallback(async (files: File[]) => {
    if (!task?.id || !user?.id) {
      toast.error('Nessun task selezionato');
      return;
    }

    const newUploadingFiles = { ...uploadingFiles };

    for (const file of files) {
      try {
        newUploadingFiles[file.name] = 0;
        setUploadingFiles(newUploadingFiles);

        const attachment = await uploadTaskAttachment(task.id, file);

        if (attachment) {
          setUploadingFiles(prev => {
            const newState = { ...prev };
            delete newState[file.name];
            return newState;
          });

          toast.success(`File caricato: ${file.name}`);

          updateLocalTask(task.id, {
            attachments: [...(task.attachments || []), attachment]
          });
        }
      } catch (error) {
        setUploadingFiles(prev => {
          const newState = { ...prev };
          delete newState[file.name];
          return newState;
        });

        toast.error(`Errore caricamento: ${file.name}`);
      }
    }
  }, [task?.id, user?.id, uploadTaskAttachment, toast, uploadingFiles, updateLocalTask]);

  // File upload configuration - Comment attachments
  const handleCommentFileDrop = useCallback((files: File[]) => {
    const maxFiles = 5;
    const newFiles = [...pendingCommentFiles, ...files].slice(0, maxFiles);
    setPendingCommentFiles(newFiles);
  }, [pendingCommentFiles]);

  // Dual-zone drop handler
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (dragOverZone === 'comment') {
      handleCommentFileDrop(acceptedFiles);
    } else {
      handleTaskFileDrop(acceptedFiles);
    }
    setDragOverZone(null);
  }, [dragOverZone, handleCommentFileDrop, handleTaskFileDrop]);

  // Custom drag handlers for zone detection
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!commentSectionRef.current) return;

    const commentRect = commentSectionRef.current.getBoundingClientRect();
    const isOverComments = e.clientY >= commentRect.top;
    setDragOverZone(isOverComments ? 'comment' : 'task');
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only reset if leaving the drawer completely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverZone(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt']
    },
    maxSize: 50 * 1024 * 1024
  });

  // Handle attachment deletion
  const handleDeleteAttachment = async (attachmentId: string, fileName: string) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${fileName}"?`)) return;

    try {
      await deleteTaskAttachment(attachmentId);
      // Update local state removing the attachment
      if (task) {
        updateLocalTask(task.id, {
          attachments: task.attachments?.filter(a => a.id !== attachmentId)
        });
      }
      toast.success('File eliminato');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione del file');
    }
  };

  // I commenti vivono nello store: la prop `task` e' una copia che alcune viste
  // (Planning, Dashboard progetti) non riallineano dopo l'inserimento, e li' il
  // commento appena scritto non compariva finche' non si riapriva la task.
  const commentiTask = useMemo<TaskComment[]>(() => {
    if (!task?.id) return [];
    const daElenco = tasks.find(t => t.id === task.id);
    if (daElenco?.comments) return daElenco.comments;
    for (const progetto of projects) {
      const annidata = progetto.tasks?.find(t => t.id === task.id);
      if (annidata?.comments) return annidata.comments;
    }
    return task.comments || [];
  }, [tasks, projects, task]);

  // Utility functions
  // Filter comments based on hideSystemComments preference
  const filterComments = (comments: TaskComment[]) => {
    if (!hideSystemComments) return comments;
    return comments.filter(comment =>
      !(comment.author?.email === 'system' ||
        comment.is_system_comment ||
        comment.content?.startsWith('[Sistema]'))
    );
  };

  const getFileIcon = (type: string | null | undefined) => {
    if (!type) return <FileText className="h-5 w-5 text-gray-500" />;
    if (type.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />;
    if (type.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (type.includes('zip') || type.includes('rar')) return <FileArchive className="h-5 w-5 text-yellow-500" />;
    if (type.includes('html') || type.includes('javascript') || type.includes('css')) return <FileCode className="h-5 w-5 text-green-500" />;
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if task was created by a client from the portal
  const isClientRequest = !!task?.is_client_request;

  // Event handlers
  // Campi personalizzati: definizioni per progetto (projects.metadata.task_custom_fields),
  // valori sulla task (tasks.custom_fields). Spunto Trello, partner Unidevs.
  const customFieldDefs: TaskCustomFieldDef[] =
    (task?.project_id && selectedProject?.id === task.project_id
      ? selectedProject?.metadata?.task_custom_fields
      : task?.project?.metadata?.task_custom_fields) || [];

  const saveCustomFieldDefs = async (defs: TaskCustomFieldDef[]) => {
    const projectId = task?.project_id;
    if (!projectId) return;
    const baseMetadata = (selectedProject?.id === projectId ? selectedProject?.metadata : task?.project?.metadata) || {};
    const metadata = { ...baseMetadata, task_custom_fields: defs };
    const saved = await updateProject(projectId, { metadata } as any, true);
    if (!saved) {
      toast.error('Errore nel salvataggio dei campi personalizzati');
      return;
    }
    // Le card leggono le definizioni dal progetto embedded nella task: allinea lo store locale
    useProjectStore.setState(state => ({
      tasks: state.tasks.map(t =>
        t.project_id === projectId && t.project ? { ...t, project: { ...t.project, metadata } } : t
      ),
      selectedProject: state.selectedProject?.id === projectId ? { ...state.selectedProject, metadata } : state.selectedProject
    }));
  };

  const handleCustomFieldValue = async (fieldId: string, value: string | number | boolean | null) => {
    if (!task?.id) return;
    // Base = valore più recente nello store, non la prop `task`: due modifiche ravvicinate
    // (blur di un campo + cambio del successivo) altrimenti si sovrascrivono a vicenda.
    const latest = useProjectStore.getState().tasks.find(t => t.id === task.id);
    const previous = latest?.custom_fields || task.custom_fields || {};
    const next = { ...previous, [fieldId]: value };
    updateLocalTask(task.id, { custom_fields: next });
    try {
      await onUpdate(task.id, { custom_fields: next });
    } catch (error) {
      console.error('Errore aggiornamento campo personalizzato:', error);
      toast.error('Errore aggiornamento campo personalizzato');
      updateLocalTask(task.id, { custom_fields: previous });
    }
  };

  const handleFieldChange = async (field: keyof Task, value: any) => {
    if (!task?.id || !user?.id) return;

    // Date svuotate dall'input: il form tiene '', task e DB vogliono null (timestamptz)
    const taskValue = (field === 'due_date' || field === 'start_date') && value === '' ? null : value;

    try {
      // Crea immediatamente un commento di attività locale per feedback visuale
      const oldValue = task[field];
      if (oldValue !== value) {
        createLocalActivityComment(field, oldValue, value);
      }

      // Update local form state immediately (ma NON per description per evitare race condition)
      if (field !== 'description') {
        setFormState(prev => ({ ...prev, [field]: value }));
        // Update local task state
        updateLocalTask(task.id, { [field]: taskValue });
      }

      // Update on server (projectStore.updateTask emette gli eventi per le notifiche)
      await onUpdate(task.id, { [field]: taskValue });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Errore aggiornamento ${field}`);

      // Revert form state on error
      setFormState(prev => ({ ...prev, [field]: task[field] }));
      updateLocalTask(task.id, { [field]: task[field] });
    }
  };

  // Funzione per creare commenti di attività locali per feedback immediato
  const createLocalActivityComment = (field: keyof Task, oldValue: any, newValue: any) => {
    if (!task?.id || !user?.id) return;

    let commentText = '';
    const userName = user.user_metadata?.full_name || user.email || 'Utente';
    const timestamp = new Date().toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    switch (field) {
      case 'status':
        const getStatusText = (status: string) => {
          switch (status) {
            case 'todo': return 'Da Fare';
            case 'in_progress': return 'In Corso';
            case 'review': return 'In Revisione';
            case 'completed': return 'Completato';
            default: return status;
          }
        };
        commentText = `**${userName}** ha cambiato lo stato da **${getStatusText(oldValue)}** a **${getStatusText(newValue)}** il ${timestamp}`;
        break;
      case 'priority':
        const getPriorityText = (priority: string) => {
          switch (priority) {
            case 'low': return 'Bassa';
            case 'medium': return 'Media';
            case 'high': return 'Alta';
            case 'urgent': return 'Urgente';
            default: return priority;
          }
        };
        commentText = `**${userName}** ha cambiato la priorità da **${getPriorityText(oldValue)}** a **${getPriorityText(newValue)}** il ${timestamp}`;
        break;
      case 'assigned_to':
        if (oldValue && newValue) {
          commentText = `**${userName}** ha riassegnato il task il ${timestamp}`;
        } else if (newValue) {
          commentText = `**${userName}** ha assegnato il task il ${timestamp}`;
        } else {
          commentText = `**${userName}** ha rimosso l'assegnazione del task il ${timestamp}`;
        }
        break;
      case 'start_date':
        if (oldValue && newValue) {
          const oldDate = new Date(oldValue).toLocaleDateString('it-IT');
          const newDate = new Date(newValue).toLocaleDateString('it-IT');
          commentText = `**${userName}** ha cambiato la data di inizio dal ${oldDate} al ${newDate} il ${timestamp}`;
        } else if (newValue) {
          const newDate = new Date(newValue).toLocaleDateString('it-IT');
          commentText = `**${userName}** ha impostato la data di inizio al ${newDate} il ${timestamp}`;
        } else {
          commentText = `**${userName}** ha rimosso la data di inizio il ${timestamp}`;
        }
        break;
      case 'due_date':
        if (oldValue && newValue) {
          const oldDate = new Date(oldValue).toLocaleDateString('it-IT');
          const newDate = new Date(newValue).toLocaleDateString('it-IT');
          commentText = `**${userName}** ha cambiato la scadenza dal ${oldDate} al ${newDate} il ${timestamp}`;
        } else if (newValue) {
          const newDate = new Date(newValue).toLocaleDateString('it-IT');
          commentText = `**${userName}** ha impostato la scadenza al ${newDate} il ${timestamp}`;
        } else {
          commentText = `**${userName}** ha rimosso la scadenza il ${timestamp}`;
        }
        break;
      case 'title':
        commentText = `**${userName}** ha cambiato il titolo da "**${oldValue}**" a "**${newValue}**" il ${timestamp}`;
        break;
      case 'section':
        if (oldValue && newValue) {
          const oldSection = availableSections.find(s => s.id === oldValue);
          const newSection = availableSections.find(s => s.id === newValue);
          commentText = `**${userName}** ha spostato il task dalla sezione "**${oldSection?.title || oldValue}**" a "**${newSection?.title || newValue}**" il ${timestamp}`;
        } else if (newValue) {
          const newSection = availableSections.find(s => s.id === newValue);
          commentText = `**${userName}** ha assegnato il task alla sezione "**${newSection?.title || newValue}**" il ${timestamp}`;
        } else {
          commentText = `**${userName}** ha rimosso il task dalla sezione il ${timestamp}`;
        }
        break;
      default:
        return; // Non creare commento per altri campi
    }

    if (commentText) {
      // Crea un commento locale temporaneo
      const localComment = {
        id: `local-${Date.now()}`,
        content: commentText,
        created_at: new Date().toISOString(),
        is_system_comment: true,
        author: {
          id: user.id,
          email: user.email || '',
          full_name: user.user_metadata?.full_name || user.email || 'Utente'
        }
      };

      // Aggiorna immediatamente l'UI locale
      updateLocalTask(task.id, {
        comments: [...(task.comments || []), localComment]
      });
    }
  };

  const handleTitleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!task?.id || !user?.id) return;

    if (formState.title.trim() && formState.title !== task.title) {
      try {
        await handleFieldChange('title', formState.title);
      } catch (error) {
        setFormState(prev => ({ ...prev, title: task.title }));
      }
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      setFormState(prev => ({ ...prev, title: task?.title || '' }));
      setIsEditingTitle(false);
    }
  };


  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    // Allow submission with just files even without text
    if ((!comment.trim() && pendingCommentFiles.length === 0) || !task?.id || !user?.id || isSubmittingComment) return;

    setIsSubmittingComment(true);
    try {
      let newComment: TaskComment | null = null;

      // Se ci sono file, usa createTaskCommentWithAttachments
      if (pendingCommentFiles.length > 0) {
        newComment = await createTaskCommentWithAttachments(task.id, comment, pendingCommentFiles);
      } else {
        // Altrimenti usa il metodo standard
        newComment = await onAddComment(task.id, comment);
      }

      // Se ci sono mention, crea le notifiche
      if (commentMentions.length > 0 && newComment) {
        await MentionNotificationService.createMentionNotifications(
          task.id,
          newComment.id || Math.random().toString(),
          commentMentions,
          user.id,
          task.title,
          comment,
          task.project_id
        );
      }

      setComment('');
      setCommentMentions([]);
      setPendingCommentFiles([]);

      const filesText = pendingCommentFiles.length > 0 ? ` con ${pendingCommentFiles.length} allegat${pendingCommentFiles.length === 1 ? 'o' : 'i'}` : '';
      toast.success(
        commentMentions.length > 0
          ? `Commento aggiunto${filesText} e ${commentMentions.length} ${commentMentions.length === 1 ? 'persona notificata' : 'persone notificate'}`
          : `Commento aggiunto${filesText}`
      );
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Errore aggiunta commento');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCloseDrawer = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTranslateCollabComment = async (commentId: string, sourceLang: string) => {
    if (translatingCommentIds.has(commentId) || !aiEnabled) return;
    setTranslatingCommentIds(prev => new Set(prev).add(commentId));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await supabase.functions.invoke('translate-comment', {
        body: { comment_id: commentId, comment_type: 'task_comment', target_language: 'it', source_language: sourceLang, force: false }
      });
      if (res.data?.translation?.content) {
        setCollabTranslations(prev => new Map(prev).set(commentId, res.data.translation.content));
      }
    } catch (e) {
      console.error('Error translating collaborator comment:', e);
    } finally {
      setTranslatingCommentIds(prev => { const s = new Set(prev); s.delete(commentId); return s; });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!task?.id) return;

    if (!window.confirm('Sei sicuro di voler eliminare questo commento?')) {
      return;
    }

    try {
      await deleteTaskComment(commentId, task.id);
      toast.success('Commento eliminato');
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast.error(error.message || 'Errore eliminazione commento');
    }
  };

  const handleEditComment = (comment: TaskComment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);

    // Estrai le menzioni esistenti dal contenuto
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions: string[] = [];
    let match;
    while ((match = mentionRegex.exec(comment.content)) !== null) {
      mentions.push(match[2]); // ID utente
    }
    setEditingCommentMentions(mentions);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
    setEditingCommentMentions([]);
  };

  const handleUpdateComment = async (commentId: string, oldContent: string) => {
    if (!task?.id || !editingCommentContent.trim()) return;

    try {
      await updateTaskComment(commentId, task.id, editingCommentContent, oldContent);
      toast.success('Commento modificato');
      handleCancelEdit();
    } catch (error: any) {
      console.error('Error updating comment:', error);
      toast.error(error.message || 'Errore modifica commento');
    }
  };

  if (!task?.id || !isOpen || !user?.id) return null;

  return (
    <>
    <LightModeWrapper>
      <AnimatePresence mode="wait">
        {isOpen && (
          <>
            {/* Overlay with fade animation */}
            <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[10000]" 
            onClick={handleCloseDrawer}
          />

          {/* Drawer with slide animation */}
          <motion.div
            ref={drawerRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            style={{
              width: isExpanded ? '100%' : windowWidth > 640 ? `${drawerWidth}vw` : '100%'
            }}
            className={`fixed inset-y-0 right-0 bg-white dark:bg-gray-900 shadow-xl z-[10001] flex flex-col border-l border-[rgba(55,53,47,0.09)] dark:border-gray-700`}
            {...getRootProps()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {/* Resize Handle - visible only on desktop and when not expanded */}
            {!isExpanded && windowWidth > 640 && (
              <div
                onMouseDown={handleResizeStart}
                className={`absolute top-0 bottom-0 cursor-col-resize z-[10002] group ${
                  isResizing ? '' : ''
                }`}
                style={{ left: -4, width: 8 }}
              >
                {/* Visible line on hover/drag */}
                <div className={`absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 transition-all ${
                  isResizing ? 'bg-[#0f6cbd] opacity-100' : 'bg-blue-400 opacity-0 group-hover:opacity-100'
                }`} />
                {/* Visual indicator dots */}
                <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1 transition-opacity pointer-events-none ${
                  isResizing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                  <div className="w-1 h-1 rounded-full bg-[#0f6cbd]" />
                  <div className="w-1 h-1 rounded-full bg-[#0f6cbd]" />
                  <div className="w-1 h-1 rounded-full bg-[#0f6cbd]" />
                </div>
              </div>
            )}
            <input {...getInputProps()} />
            {/* Task Zone Overlay - shown when dragging over non-comment area */}
            {isDragActive && dragOverZone === 'task' && (
              <div
                className="absolute inset-0 bg-[#0f6cbd] bg-opacity-20 border-4 border-blue-500 border-dashed rounded-lg z-50 flex items-center justify-center backdrop-blur-sm pointer-events-none"
                style={{ bottom: commentSectionRef.current ? `${window.innerHeight - commentSectionRef.current.getBoundingClientRect().top}px` : 0 }}
              >
                <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl">
                  <Upload className="mx-auto h-16 w-16 text-blue-500 animate-bounce" />
                  <p className="mt-3 text-base font-semibold text-blue-600 dark:text-blue-400">
                    Rilascia per allegare alla task
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Il file verrà aggiunto agli allegati
                  </p>
                </div>
              </div>
            )}

            {/* Header - sfondo grigio sidebar */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-1.5 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-[#F7F6F3] dark:bg-[#373C3F]">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                {/* Status & Priority indicators */}
                <div className="flex items-center space-x-1">
                  <div className={`w-2 h-2 rounded-full ${
                    task.status === 'completed' ? 'bg-green-500' :
                    task.status === 'in_progress' ? 'bg-[#0f6cbd]' :
                    task.status === 'review' ? 'bg-yellow-500' : 'bg-gray-300'
                  }`} />
                  <div className={`w-1 h-3 rounded-sm ${
                    task.priority === 'urgent' ? 'bg-red-500' :
                    task.priority === 'high' ? 'bg-orange-500' :
                    task.priority === 'medium' ? 'bg-yellow-500' :
                    task.priority === 'low' ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                </div>
                
                {/* Title */}
                <div className="flex-1 min-w-0">
                  {isEditingTitle ? (
                    <form onSubmit={handleTitleSubmit} className="flex-1">
                      <input
                        ref={titleInputRef}
                        type="text"
                        value={formState.title}
                        onChange={(e) => setFormState(prev => ({ ...prev, title: e.target.value }))}
                        onBlur={handleTitleSubmit}
                        onKeyDown={handleTitleKeyDown}
                        className="w-full px-2 py-1 text-sm bg-white dark:bg-gray-700 text-[#37352F] dark:text-white border border-[rgba(55,53,47,0.09)] dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                      />
                    </form>
                  ) : (
                    <div
                      className="flex items-center cursor-pointer group"
                      onClick={() => setIsEditingTitle(true)}
                    >
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{formState.title}</h2>
                      <Edit2 className="h-3 w-3 ml-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-2 p-1 bg-transparent text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded transition-colors"
                title={isExpanded ? 'Riduci' : 'Espandi a schermo intero'}
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>

              <button
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set('task', task.id);
                  navigator.clipboard.writeText(url.toString());
                  toast.success('Link copiato');
                }}
                className="ml-2 p-1 bg-transparent text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded transition-colors"
                title="Copia link task"
              >
                <Link2 className="h-4 w-4" />
              </button>

              <button
                onClick={() => setShowShareToChat(true)}
                className="ml-2 p-1 bg-transparent text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded transition-colors"
                title="Condividi in chat"
              >
                <MessageSquareShare className="h-4 w-4" />
              </button>

              <button
                onClick={onClose}
                className="ml-2 p-1 bg-transparent text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Quick Actions Bar - Notion Style con contenitori grigi - sfondo sidebar */}
            <div className="px-3 py-2.5 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-[#F7F6F3] dark:bg-[#373C3F] text-xs">
              <div className="grid grid-cols-[160px_140px_1fr_auto] gap-x-3 gap-y-2 items-center">
                {/* Riga 1 */}
                {/* Status */}
                <div className="relative inline-flex items-center bg-[#F1F1EF] dark:bg-gray-800 rounded-md">
                  <div className={`absolute left-2.5 w-2 h-2 rounded-full pointer-events-none ${getStatusColor(formState.status)}`} />
                  <select
                    className="w-full pl-6 pr-2 py-1.5 bg-transparent text-[#37352F] dark:text-gray-100 rounded-md hover:bg-[#EAEAEA] dark:hover:bg-gray-700 focus:outline-none cursor-pointer appearance-none transition-colors"
                    value={formState.status}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                  >
                    <option value="todo">Da Fare</option>
                    <option value="in_progress">In Corso</option>
                    <option value="review">In Revisione</option>
                    <option value="completed">Completato</option>
                    <option value="standby">In attesa</option>
                  </select>
                </div>

                {/* Priority */}
                <div className="relative inline-flex items-center bg-[#F1F1EF] dark:bg-gray-800 rounded-md">
                  <div className={`absolute left-2.5 w-1 h-3 rounded-sm pointer-events-none ${getPriorityColor(formState.priority || 'medium')}`} />
                  <select
                    className="w-full pl-6 pr-2 py-1.5 bg-transparent text-[#37352F] dark:text-gray-100 rounded-md hover:bg-[#EAEAEA] dark:hover:bg-gray-700 focus:outline-none cursor-pointer appearance-none transition-colors"
                    value={formState.priority}
                    onChange={(e) => handleFieldChange('priority', e.target.value)}
                  >
                    <option value="low">Bassa</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>

                {/* Assignee - Dropdown unificato (utenti + clienti) */}
                <div className="relative" ref={userDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="w-full flex items-center bg-[#F1F1EF] dark:bg-gray-800 rounded-md pl-6 pr-2 py-1.5 hover:bg-[#EAEAEA] dark:hover:bg-gray-700 transition-colors text-left"
                  >
                    <User className="absolute left-2 h-3 w-3 text-[#787774]" />
                    <span className="text-[#37352F] dark:text-gray-100 truncate">
                      {formState.assigned_client_access_id
                        ? projectClientAccesses.find(c => c.id === formState.assigned_client_access_id)?.client?.business_name ||
                          projectClientAccesses.find(c => c.id === formState.assigned_client_access_id)?.username ||
                          'Cliente'
                        : formState.assigned_to
                          ? users.find(u => u.id === formState.assigned_to)?.full_name ||
                            users.find(u => u.id === formState.assigned_to)?.email ||
                            'Non assegnato'
                          : 'Non assegnato'}
                    </span>
                    <ChevronDown className="ml-auto h-3 w-3 text-[#787774] flex-shrink-0" />
                  </button>

                  {/* Dropdown */}
                  {isUserDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 z-50 overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                        <input
                          type="text"
                          placeholder="Cerca..."
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          className="w-full px-2 py-1.5 text-xs bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 rounded border-none focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                          autoFocus
                        />
                      </div>
                      {/* Combined list */}
                      <div className="max-h-80 overflow-y-auto">
                        {/* Non assegnato */}
                        <button
                          type="button"
                          onClick={async () => {
                            // Clear both assignments
                            if (formState.assigned_client_access_id) {
                              await assignTaskToClient(task.id, null);
                              setFormState(prev => ({ ...prev, assigned_client_access_id: '' }));
                            }
                            handleFieldChange('assigned_to', null);
                            setIsUserDropdownOpen(false);
                            setUserSearchQuery('');
                          }}
                          className={`w-full px-3 py-2 text-left text-xs hover:bg-[#F7F6F3] dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                            !formState.assigned_to && !formState.assigned_client_access_id ? 'bg-[#F7F6F3] dark:bg-gray-700' : ''
                          }`}
                        >
                          <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                            <User className="h-3 w-3 text-gray-500" />
                          </div>
                          <span className="text-[#787774]">Non assegnato</span>
                        </button>

                        {/* Team section header */}
                        <div className="px-3 py-1.5 text-[10px] font-medium text-[#787774] uppercase tracking-wider bg-[#F7F6F3] dark:bg-gray-700/50">
                          Team
                        </div>

                        {/* Users */}
                        {users
                          .filter(u =>
                            (u.full_name?.toLowerCase() || '').includes(userSearchQuery.toLowerCase()) ||
                            (u.email?.toLowerCase() || '').includes(userSearchQuery.toLowerCase())
                          )
                          .map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={async () => {
                                // Clear client assignment if any, then set user
                                if (formState.assigned_client_access_id) {
                                  await assignTaskToClient(task.id, null);
                                  setFormState(prev => ({ ...prev, assigned_client_access_id: '' }));
                                }
                                handleFieldChange('assigned_to', u.id);
                                setIsUserDropdownOpen(false);
                                setUserSearchQuery('');
                              }}
                              className={`w-full px-3 py-2 text-left text-xs hover:bg-[#F7F6F3] dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                                formState.assigned_to === u.id && !formState.assigned_client_access_id ? 'bg-[#F7F6F3] dark:bg-gray-700' : ''
                              }`}
                            >
                              <div className="w-5 h-5 rounded-full bg-[#0f6cbd] flex items-center justify-center text-white text-[10px] font-medium">
                                {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <span className="text-[#37352F] dark:text-gray-100 truncate">
                                {u.full_name || u.email}
                              </span>
                            </button>
                          ))}

                        {/* Collaborators section */}
                        {projectCollaboratorAccesses.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] font-medium text-[#787774] uppercase tracking-wider bg-[#F7F6F3] dark:bg-gray-700/50 mt-1">
                              Partner
                            </div>

                            {projectCollaboratorAccesses
                              .filter(c =>
                                (c.partner?.name?.toLowerCase() || '').includes(userSearchQuery.toLowerCase()) ||
                                (c.username?.toLowerCase() || '').includes(userSearchQuery.toLowerCase())
                              )
                              .map((collab) => (
                                <button
                                  key={`collab-${collab.id}`}
                                  type="button"
                                  onClick={async () => {
                                    const { CollaboratorAuthService } = await import('../../lib/services/collaboratorAuthService');
                                    if (formState.assigned_to) {
                                      handleFieldChange('assigned_to', null);
                                    }
                                    if (formState.assigned_client_access_id) {
                                      await assignTaskToClient(task.id, null);
                                      setFormState(prev => ({ ...prev, assigned_client_access_id: '' }));
                                    }
                                    await CollaboratorAuthService.assignTaskToCollaborator(task.id, collab.id);
                                    // Update local task state
                                    handleFieldChange('assigned_collaborator_access_id', collab.id);
                                    setIsUserDropdownOpen(false);
                                    setUserSearchQuery('');
                                  }}
                                  className="w-full px-3 py-2 text-left text-xs hover:bg-[#F7F6F3] dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                                >
                                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-white text-[10px] font-medium">
                                    {(collab.partner?.name || collab.username || '?').charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-[#37352F] dark:text-gray-100 truncate">
                                    {collab.partner?.name || collab.username}
                                  </span>
                                  {collab.preferred_language !== 'it' && (
                                    <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded ml-auto flex-shrink-0">
                                      {collab.preferred_language.toUpperCase()}
                                    </span>
                                  )}
                                </button>
                              ))}
                          </>
                        )}

                        {/* Clients section - only show if there are clients with access */}
                        {projectClientAccesses.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] font-medium text-[#787774] uppercase tracking-wider bg-[#F7F6F3] dark:bg-gray-700/50 mt-1">
                              Clienti
                            </div>

                            {projectClientAccesses
                              .filter(c =>
                                (c.client?.business_name?.toLowerCase() || '').includes(userSearchQuery.toLowerCase()) ||
                                (c.username?.toLowerCase() || '').includes(userSearchQuery.toLowerCase())
                              )
                              .map((client) => (
                                <button
                                  key={`client-${client.id}`}
                                  type="button"
                                  onClick={async () => {
                                    if (formState.assigned_to) {
                                      handleFieldChange('assigned_to', null);
                                    }
                                    await assignTaskToClient(task.id, client.id);
                                    setFormState(prev => ({ ...prev, assigned_client_access_id: client.id }));
                                    setIsUserDropdownOpen(false);
                                    setUserSearchQuery('');
                                  }}
                                  className={`w-full px-3 py-2 text-left text-xs hover:bg-[#F7F6F3] dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                                    formState.assigned_client_access_id === client.id ? 'bg-[#F7F6F3] dark:bg-gray-700' : ''
                                  }`}
                                >
                                  <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[10px] font-medium">
                                    {(client.client?.business_name || client.username || '?').charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-[#37352F] dark:text-gray-100 truncate">
                                    {client.client?.business_name || client.username}
                                  </span>
                                </button>
                              ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Delete button */}
                <button
                  onClick={() => onDelete(task.id)}
                  className="p-1.5 text-[#787774] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                  title="Elimina task"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                {/* Riga 2 */}
                {/* Combined Date Field with Dropdown */}
                <div className="col-span-2 relative" ref={dateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                    className="w-full flex items-center bg-[#F1F1EF] dark:bg-gray-800 rounded-md pl-2 pr-2 py-1.5 hover:bg-[#EAEAEA] dark:hover:bg-gray-700 transition-colors text-left"
                  >
                    {activeDateTab === 'due' ? (
                      <Calendar className="h-3 w-3 text-[#787774] mr-2 flex-shrink-0" />
                    ) : (
                      <Clock className="h-3 w-3 text-[#787774] mr-2 flex-shrink-0" />
                    )}
                    <span className={`flex-1 truncate ${
                      (activeDateTab === 'due' ? formState.due_date : formState.start_date)
                        ? 'text-[#37352F] dark:text-gray-100'
                        : 'text-[#9B9A97] dark:text-gray-500'
                    }`}>
                      {(activeDateTab === 'due' ? formState.due_date : formState.start_date)
                        ? new Date(activeDateTab === 'due' ? formState.due_date : formState.start_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
                        : (activeDateTab === 'due' ? 'Scadenza' : 'Data inizio')}
                    </span>
                    <ChevronDown className="h-3 w-3 text-[#787774] ml-1 flex-shrink-0" />
                  </button>

                  {/* Date Dropdown */}
                  {isDateDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 z-50 overflow-hidden">
                      {/* Tabs */}
                      <div className="flex border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => setActiveDateTab('due')}
                          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            activeDateTab === 'due'
                              ? 'bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 border-b-2 border-[#2383E2]'
                              : 'text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 hover:bg-[#F7F6F3] dark:hover:bg-gray-700'
                          }`}
                        >
                          <Calendar className="h-3 w-3" />
                          Scadenza
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveDateTab('start')}
                          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            activeDateTab === 'start'
                              ? 'bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 border-b-2 border-[#2383E2]'
                              : 'text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 hover:bg-[#F7F6F3] dark:hover:bg-gray-700'
                          }`}
                        >
                          <Clock className="h-3 w-3" />
                          Inizio
                        </button>
                      </div>
                      {/* Date Input */}
                      <div className="p-3">
                        <input
                          type="date"
                          className="w-full px-3 py-2 text-sm bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 rounded-md border border-[rgba(55,53,47,0.09)] dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                          value={activeDateTab === 'due' ? formState.due_date : formState.start_date}
                          onChange={(e) => {
                            handleFieldChange(activeDateTab === 'due' ? 'due_date' : 'start_date', e.target.value);
                          }}
                        />
                        {/* Clear button */}
                        {(activeDateTab === 'due' ? formState.due_date : formState.start_date) && (
                          <button
                            type="button"
                            onClick={() => {
                              handleFieldChange(activeDateTab === 'due' ? 'due_date' : 'start_date', '');
                            }}
                            className="mt-2 w-full px-3 py-1.5 text-xs text-[#787774] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          >
                            Rimuovi data
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Schedula su calendario (A1) — apre il calendario visivo (CalendarModal) pre-compilato */}
                <div className="col-span-2">
                  <button
                    type="button"
                    onClick={() => setIsScheduleOpen(true)}
                    disabled={!formState.assigned_to}
                    data-tooltip-id="schedule-cal-tooltip"
                    data-tooltip-content={
                      !formState.assigned_to
                        ? 'Assegna prima la task a un utente'
                        : 'Apri il calendario per schedulare un evento sull\'assegnatario'
                    }
                    className={`w-full flex items-center rounded-md pl-2 pr-2 py-1.5 transition-colors text-left ${
                      !formState.assigned_to
                        ? 'bg-[#F7F6F3] dark:bg-gray-800 text-[#9B9A97] dark:text-gray-600 cursor-not-allowed'
                        : 'bg-[#F1F1EF] dark:bg-gray-800 text-[#37352F] dark:text-gray-100 hover:bg-[#EAEAEA] dark:hover:bg-gray-700'
                    }`}
                  >
                    <CalendarPlus className="h-3 w-3 mr-2 flex-shrink-0" style={{ color: !formState.assigned_to ? undefined : '#2383E2' }} />
                    <span className="flex-1 truncate text-xs font-medium">Schedula su calendario</span>
                  </button>
                  {!formState.assigned_to && <Tooltip id="schedule-cal-tooltip" place="top" />}
                </div>

                {/* Section */}
                {availableSections.length > 0 ? (
                  <div className="relative inline-flex items-center bg-[#F1F1EF] dark:bg-gray-800 rounded-md">
                    <Hash className="absolute left-2 h-3 w-3 text-[#787774] pointer-events-none" />
                    <select
                      className="w-full pl-6 pr-2 py-1.5 bg-transparent text-[#37352F] dark:text-gray-100 rounded-md hover:bg-[#EAEAEA] dark:hover:bg-gray-700 focus:outline-none cursor-pointer appearance-none truncate transition-colors"
                      value={formState.section}
                      onChange={(e) => handleFieldChange('section', e.target.value || null)}
                    >
                      <option value="">Sezione</option>
                      {availableSections.map((section) => (
                        <option key={section.id} value={section.id}>
                          {section.title}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div />
                )}

                {/* Recurrence Selector */}
                <RecurrenceSelector
                  value={{
                    is_recurring: formState.is_recurring,
                    recurrence_type: formState.recurrence_type,
                    recurrence_interval: formState.recurrence_interval,
                    recurrence_unit: formState.recurrence_unit,
                    recurrence_end_type: formState.recurrence_end_type,
                    recurrence_end_date: formState.recurrence_end_date,
                    recurrence_max_count: formState.recurrence_max_count,
                  }}
                  onChange={(config) => {
                    // Se si abilita ricorrenza senza scadenza, imposta oggi come default
                    const needsDueDate = config.is_recurring && !formState.due_date && !task?.due_date;
                    const today = needsDueDate ? new Date().toISOString() : undefined;

                    // Aggiorna tutti i campi di ricorrenza
                    setFormState(prev => ({
                      ...prev,
                      is_recurring: config.is_recurring,
                      recurrence_type: config.recurrence_type,
                      recurrence_interval: config.recurrence_interval,
                      recurrence_unit: config.recurrence_unit,
                      recurrence_end_type: config.recurrence_end_type,
                      recurrence_end_date: config.recurrence_end_date,
                      recurrence_max_count: config.recurrence_max_count,
                      ...(today && { due_date: today }),
                    }));
                    // Salva su server
                    if (task?.id) {
                      onUpdate(task.id, {
                        is_recurring: config.is_recurring,
                        recurrence_type: config.recurrence_type,
                        recurrence_interval: config.recurrence_interval,
                        recurrence_unit: config.recurrence_unit,
                        recurrence_end_type: config.recurrence_end_type,
                        recurrence_end_date: config.recurrence_end_date,
                        recurrence_max_count: config.recurrence_max_count,
                        // Imposta original_due_date se non già presente
                        original_due_date: task.original_due_date || task.due_date || today,
                        // Imposta due_date a oggi se mancante e si abilita ricorrenza
                        ...(today && { due_date: today }),
                      });
                    }
                  }}
                />

                {/* Promemoria scadenza a scelta e posizione (spunti Trello, partner Unidevs) */}
                <TaskReminderField
                  value={task?.reminder_days_before}
                  hasDueDate={!!formState.due_date}
                  onChange={(v) => handleFieldChange('reminder_days_before', v)}
                />
                <div />
                <TaskLocationField
                  value={task?.location}
                  onChange={(v) => handleFieldChange('location', v)}
                />
              </div>

              {/* "Creata dal Cliente" badge for client request tasks */}
              {isClientRequest && (
                <div className="mt-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                    <User className="h-3 w-3" />
                    Creata dal Cliente
                  </span>
                </div>
              )}
            </div>

            {/* Editor Toolbar - shown only in expanded mode, sticky below Quick Actions */}
            {isExpanded && (
              <div className="sticky top-0 z-10 flex gap-1 p-2 bg-[#F7F7F5] dark:bg-gray-800/50 shadow-sm border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('bold') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                >
                  <Bold className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('italic') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                >
                  <Italic className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const url = window.prompt('Enter URL');
                    if (url) {
                      editor?.chain().focus().setLink({ href: url }).run();
                    }
                  }}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('link') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                >
                  <LinkIcon className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('bulletList') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                >
                  <List className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('orderedList') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                >
                  <ListOrdered className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const url = window.prompt('URL immagine:');
                    if (url) {
                      editor?.chain().focus().setImage({ src: url }).run();
                    }
                  }}
                  className="p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700"
                  title="Inserisci immagine"
                >
                  <Image className="h-3 w-3" />
                </button>

                {/* Separator */}
                <div className="w-px h-6 bg-[rgba(55,53,47,0.16)] dark:bg-gray-600 mx-1"></div>

                {/* Headings */}
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('heading', { level: 1 }) ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                  title="Titolo 1"
                >
                  <Heading1 className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('heading', { level: 2 }) ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                  title="Titolo 2"
                >
                  <Heading2 className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-1 rounded text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('heading', { level: 3 }) ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600' : ''}`}
                  title="Titolo 3"
                >
                  <Heading3 className="h-3 w-3" />
                </button>

                {/* Separator */}
                <div className="w-px h-6 bg-[rgba(55,53,47,0.16)] dark:bg-gray-600 mx-1"></div>

                {/* Text Color */}
                <input
                  type="color"
                  onInput={(e) => editor?.chain().focus().setColor((e.target as HTMLInputElement).value).run()}
                  value={editor?.getAttributes('textStyle').color || '#000000'}
                  className="w-6 h-6 rounded cursor-pointer border-0"
                  title="Colore testo"
                />

                {/* Clear Formatting */}
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}
                  className="p-1 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-700/50 dark:hover:bg-gray-700"
                  title="Rimuovi formattazione"
                >
                  <Eraser className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Tabs - Notion style - sfondo sidebar - hidden in expanded mode */}
              {!isExpanded && (
                <div className="flex items-center gap-1 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 px-3 bg-[#F7F6F3] dark:bg-[#373C3F]">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`relative px-3 py-2 text-xs font-medium transition-colors ${
                      activeTab === 'details'
                        ? 'text-[#37352F] dark:text-gray-100'
                        : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800 rounded'
                    }`}
                  >
                    Dettagli
                    {activeTab === 'details' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#37352F] dark:bg-gray-100" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('activity')}
                    className={`relative px-3 py-2 text-xs font-medium transition-colors ${
                      activeTab === 'activity'
                        ? 'text-[#37352F] dark:text-gray-100'
                        : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800 rounded'
                    }`}
                  >
                    Attività
                    <span className="ml-1 text-[#787774] dark:text-gray-500">
                      {filterComments(commentiTask).length}
                    </span>
                    {activeTab === 'activity' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#37352F] dark:bg-gray-100" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('files')}
                    className={`relative px-3 py-2 text-xs font-medium transition-colors ${
                      activeTab === 'files'
                        ? 'text-[#37352F] dark:text-gray-100'
                        : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800 rounded'
                    }`}
                  >
                    File
                    <span className="ml-1 text-[#787774] dark:text-gray-500">
                      {task.attachments?.length || 0}
                    </span>
                    {activeTab === 'files' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#37352F] dark:bg-gray-100" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('brief')}
                    className={`relative px-3 py-2 text-xs font-medium transition-colors ${
                      activeTab === 'brief'
                        ? 'text-[#37352F] dark:text-gray-100'
                        : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800 rounded'
                    }`}
                  >
                    Brief
                    <span className="ml-1 text-[#787774] dark:text-gray-500">
                      {briefDocuments?.length || 0}
                    </span>
                    {activeTab === 'brief' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#37352F] dark:bg-gray-100" />
                    )}
                  </button>
                </div>
              )}

              <div className={`${isExpanded ? 'px-12 lg:px-24 py-6 max-w-6xl mx-auto' : 'p-3'} space-y-4 bg-white dark:bg-gray-900`}>
                {/* Tab Content */}
                {(activeTab === 'details' || isExpanded) && (
                  <>
                    {/* File Attachments Preview - Inline compact */}
                    {task.attachments && task.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {task.attachments.map((file, index) => (
                          <SignedUrl key={index} url={file.url}>
                          {(href) => (
                          <button
                            onClick={() => setPreviewFile({ url: href ?? file.url, type: file.type })}
                            className="flex items-center gap-1.5 px-2 py-1 bg-[#FBFBFA] dark:bg-gray-800 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50 rounded transition-all text-xs border border-[rgba(55,53,47,0.09)] dark:border-gray-700"
                            title={file.name}
                          >
                            {getFileIcon(file.type)}
                            <span className="text-[#787774] dark:text-gray-400 max-w-[80px] truncate">
                              {file.name}
                            </span>
                          </button>
                          )}
                          </SignedUrl>
                        ))}
                      </div>
                    )}

                    {/* Description - Notion style minimale */}
                    <div className={`${isExpanded ? '' : 'min-h-[100px]'}`}>
                      {/* Toolbar minimale */}
                      {!isExpanded && (
                        <div className="flex gap-0.5 mb-1">
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleBold().run()}
                            className={`p-1 rounded text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('bold') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600 text-[#37352F]' : ''}`}
                            title="Grassetto"
                          >
                            <Bold className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleItalic().run()}
                            className={`p-1 rounded text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('italic') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600 text-[#37352F]' : ''}`}
                            title="Corsivo"
                          >
                            <Italic className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleBulletList().run()}
                            className={`p-1 rounded text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('bulletList') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600 text-[#37352F]' : ''}`}
                            title="Lista puntata"
                          >
                            <List className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                            className={`p-1 rounded text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('orderedList') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600 text-[#37352F]' : ''}`}
                            title="Lista numerata"
                          >
                            <ListOrdered className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const url = window.prompt('URL:');
                              if (url) editor?.chain().focus().setLink({ href: url }).run();
                            }}
                            className={`p-1 rounded text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 ${editor?.isActive('link') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-600 text-[#37352F]' : ''}`}
                            title="Link"
                          >
                            <LinkIcon className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      <FluTaskOutcome metadata={task.metadata} />
                      <EditorContent
                        editor={editor}
                        className={`${isExpanded ? 'min-h-[600px] px-12 lg:px-24 py-6' : 'min-h-[100px] py-2'} px-0 focus:outline-none ${isExpanded ? 'text-base' : 'text-sm'} text-[#37352F] dark:text-gray-100 [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-[#9B9A97] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none ${isModKeyPressed ? '[&_.ProseMirror_a]:cursor-pointer' : ''}`}
                      />
                      <Tooltip
                        id="task-editor-link-hint"
                        place="top"
                        className="!bg-[#37352F] dark:!bg-gray-800 !text-white !text-xs !rounded-md !px-2.5 !py-1.5 !z-[9999]"
                      />
                    </div>

                    {/* Checklist - Espandibile */}
                    {/* Sempre visibile: anche a checklist vuota serve per aggiungere la prima
                        voce, copiarla da un'altra task o farla generare all'AI */}
                    {(() => {
                      const checklistItems = task.checklist || [];
                      const completedCount = checklistItems.filter(item => item.is_completed).length;
                      const totalCount = checklistItems.length;
                      const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

                      return (
                        <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg overflow-hidden">
                          {/* Header cliccabile */}
                          <button
                            type="button"
                            onClick={() => setIsChecklistExpanded(!isChecklistExpanded)}
                            className="w-full flex items-center justify-between p-3 bg-[#FBFBFA] dark:bg-gray-800 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center space-x-2">
                              <CheckSquare className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                              <span className="text-sm font-medium text-[#37352F] dark:text-gray-100">
                                Checklist
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                totalCount > 0 && completedCount === totalCount
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {completedCount}/{totalCount}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {/* Progress bar mini */}
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-300 ${
                                    totalCount > 0 && completedCount === totalCount ? 'bg-green-500' : 'bg-[#0f6cbd]'
                                  }`}
                                  style={{ width: `${progressPercent}%` }}
                                />
                              </div>
                              {isChecklistExpanded ? (
                                <ChevronUp className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                              )}
                            </div>
                          </button>

                          {/* Contenuto espandibile */}
                          <AnimatePresence>
                            {isChecklistExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="p-3 pt-0">
                                  <TaskChecklist
                                    checklist={checklistItems}
                                    taskId={task.id}
                                    onToggleItem={toggleChecklistItem}
                                    onCreateItem={createTaskChecklistItem}
                                    onUpdateItem={updateTaskChecklistItem}
                                    onDeleteItem={deleteTaskChecklistItem}
                                    hideHeader
                                    sourceContext={{
                                      title: task.title,
                                      description: task.description || undefined,
                                      projectName: selectedProject?.name || task.project?.name || undefined
                                    }}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                        </div>
                      );
                    })()}

                    {/* Campi personalizzati della card (definiti per progetto) */}
                    {task.project_id && (
                      <TaskCustomFields
                        definitions={customFieldDefs}
                        values={(task.custom_fields || {}) as Record<string, string | number | boolean | null>}
                        canManage
                        onChangeValue={handleCustomFieldValue}
                        onSaveDefinitions={saveCustomFieldDefs}
                      />
                    )}

                    {/* Il tempo non dipende dalla checklist: sta fuori dal blocco sopra */}
                    {task.project_id && (
                      <TaskTimeTracker taskId={task.id} projectId={task.project_id} />
                    )}

                    {/* Dependencies Section */}
                    {task.dependencies && task.dependencies.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-[#787774] dark:text-gray-300 mb-2">
                          Dipendenze
                        </label>
                        <div className="space-y-2">
                          {task.dependencies.map((depId) => {
                            const depTask = tasks.find(t => t.id === depId);
                            if (!depTask) return null;
                            return (
                              <div
                                key={depId}
                                className="flex items-center space-x-2 p-2 bg-[#FBFBFA] dark:bg-gray-800 rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 cursor-pointer hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700 transition-colors"
                              >
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  depTask.status === 'completed' ? 'bg-green-400' : 'bg-gray-300 dark:bg-gray-500'
                                }`} />
                                <span className="text-sm text-[#37352F] dark:text-gray-100 flex-1 truncate">
                                  {depTask.title}
                                </span>
                                {depTask.status === 'completed' && (
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {task.dependencies.some(depId => {
                          const depTask = tasks.find(t => t.id === depId);
                          return depTask && depTask.status !== 'completed';
                        }) && (
                          <div className="mt-2 flex items-center space-x-1 text-xs text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="h-3 w-3" />
                            <span>Ci sono dipendenze non completate</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Client Comments Section - shown when there are client comments or for client request tasks */}
                    {(taskClientComments.length > 0 || isClientRequest) && (
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-xs font-medium text-purple-600 dark:text-purple-400 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            Messaggi Cliente ({taskClientComments.length})
                          </label>
                          {taskClientComments.filter(c => c.author_type === 'client' && !c.is_read).length > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">
                              {taskClientComments.filter(c => c.author_type === 'client' && !c.is_read).length} nuovi
                            </span>
                          )}
                        </div>

                        {/* Client Comments List - Chat Style */}
                        <div className="space-y-3 max-h-[300px] overflow-y-auto mb-3 px-1">
                          {taskClientCommentsLoading ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                            </div>
                          ) : taskClientComments.length === 0 ? (
                            <div className="text-center py-4 text-xs text-[#787774] dark:text-gray-500">
                              Nessun messaggio. Scrivi al cliente qui sotto.
                            </div>
                          ) : (
                            <>
                            {taskClientComments.map((clientComment) => {
                              const isStaff = clientComment.author_type === 'staff';
                              return (
                                <div
                                  key={clientComment.id}
                                  className={`flex gap-2 ${isStaff ? 'flex-row-reverse' : ''}`}
                                >
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    isStaff
                                      ? 'bg-[#D3E5EF] dark:bg-[#294350]'
                                      : 'bg-[#E8DEEE] dark:bg-[#3F2E4E]'
                                  }`}>
                                    <User className={`w-3.5 h-3.5 ${
                                      isStaff
                                        ? 'text-[#337EA9] dark:text-[#529CCA]'
                                        : 'text-[#9065B0] dark:text-[#B395C9]'
                                    }`} />
                                  </div>
                                  <div className={`flex-1 max-w-[80%] ${isStaff ? 'text-right' : ''}`}>
                                    <div className={`inline-block rounded-lg px-3 py-2 ${
                                      isStaff
                                        ? 'bg-[#D3E5EF] dark:bg-[#294350] text-[#37352F] dark:text-white'
                                        : 'bg-[#E8DEEE] dark:bg-[#3F2E4E] text-[#37352F] dark:text-white'
                                    }`}>
                                      <div className="text-[10px] font-medium mb-0.5 text-[#787774] dark:text-[#999999]">
                                        {clientComment.author_name || (isStaff ? 'Team' : 'Cliente')}
                                      </div>
                                      <div className="whitespace-pre-wrap text-xs">
                                        <ExpandableComment maxHeight={80} fadeClassName={isStaff ? 'from-[#D3E5EF] dark:from-[#294350]' : 'from-[#E8DEEE] dark:from-[#3F2E4E]'}>
                                          {clientComment.content}
                                        </ExpandableComment>
                                      </div>
                                      {/* Client comment attachments */}
                                      {clientComment.attachments && clientComment.attachments.length > 0 && (
                                        <CommentAttachments
                                          attachments={clientComment.attachments as any}
                                          disabled={true}
                                        />
                                      )}
                                      {/* Transcribe button for audio comments */}
                                      {AUDIO_TRANSCRIPTION_LEGACY_ENABLED &&
                                        clientComment.content === '🎤 Messaggio vocale' &&
                                        clientComment.attachments?.some((a: any) => a.type?.startsWith('audio/')) && (
                                        <button
                                          onClick={async () => {
                                            const audioAttachment = clientComment.attachments?.find((a: any) => a.type?.startsWith('audio/'));
                                            if (!audioAttachment) return;
                                            setTranscribingCommentIds(prev => new Set(prev).add(clientComment.id));
                                            try {
                                              const text = await transcribeClientComment(clientComment.id, (audioAttachment as any).url);
                                              if (!text) {
                                                toast.error('Nessun testo rilevato nell\'audio');
                                              }
                                            } catch {
                                              toast.error('Errore nella trascrizione');
                                            } finally {
                                              setTranscribingCommentIds(prev => {
                                                const next = new Set(prev);
                                                next.delete(clientComment.id);
                                                return next;
                                              });
                                            }
                                          }}
                                          disabled={transcribingCommentIds.has(clientComment.id)}
                                          className="flex items-center gap-1 mt-1.5 px-2 py-1 text-[10px] font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded transition-colors disabled:opacity-50"
                                        >
                                          {transcribingCommentIds.has(clientComment.id) ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Languages className="w-3 h-3" />
                                          )}
                                          {transcribingCommentIds.has(clientComment.id) ? 'Trascrizione...' : 'Trascrivi'}
                                        </button>
                                      )}
                                    </div>
                                    <div className={`text-[10px] text-[#787774] dark:text-[#999999] mt-0.5 ${isStaff ? 'text-right' : ''}`}>
                                      {new Date(clientComment.created_at).toLocaleDateString('it-IT', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            <div ref={clientCommentsEndRef} />
                            </>
                          )}
                        </div>

                        {/* Reply to Client Input */}
                        <div className="space-y-1.5">
                          <div className="flex items-end gap-2">
                            <textarea
                              value={clientCommentReply}
                              onChange={(e) => setClientCommentReply(e.target.value)}
                              placeholder="Rispondi al cliente..."
                              className="flex-1 px-3 py-2 text-xs bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-[#787774] dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none overflow-y-auto max-h-[80px]"
                              rows={2}
                            />
                            <button
                              onClick={async () => {
                                if (!clientCommentReply.trim() || isSubmittingClientReply || !task?.id) return;
                                setIsSubmittingClientReply(true);
                                const currentUser = users.find(u => u.id === user?.id);
                                const authorName = currentUser?.full_name || currentUser?.email || 'Team';
                                const success = await replyToClientComment(task.id, clientCommentReply.trim(), authorName, notifyClientOnReply);
                                if (success) {
                                  setClientCommentReply('');
                                  setNotifyClientOnReply(false);
                                  toast.success(notifyClientOnReply ? 'Risposta inviata e cliente notificato' : 'Risposta inviata');
                                } else {
                                  toast.error('Errore nell\'invio della risposta');
                                }
                                setIsSubmittingClientReply(false);
                              }}
                              disabled={!clientCommentReply.trim() || isSubmittingClientReply}
                              className="p-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isSubmittingClientReply ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setNotifyClientOnReply(prev => !prev)}
                            className={`flex items-center gap-1.5 text-[11px] transition-colors ${
                              notifyClientOnReply
                                ? 'text-purple-600 dark:text-purple-400'
                                : 'text-[#787774] dark:text-gray-500 hover:text-[#37352F] dark:hover:text-gray-300'
                            }`}
                          >
                            <BellRing className="h-3 w-3" />
                            <span>Notifica il cliente</span>
                            <div className={`w-6 h-3.5 rounded-full transition-colors ${notifyClientOnReply ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                              <div className={`w-2.5 h-2.5 rounded-full bg-white transition-transform transform ${notifyClientOnReply ? 'translate-x-3' : 'translate-x-0.5'} mt-[1px]`} />
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Comments Section in Details Tab - hidden in expanded mode */}
                    {!isExpanded && (
                      <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-xs font-medium text-[#787774] dark:text-gray-300">
                          Commenti ({filterComments(commentiTask).length})
                        </label>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => setHideSystemComments(prev => !prev)}
                            className="flex items-center space-x-2 text-xs text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 transition-colors"
                            title={hideSystemComments ? 'Mostra commenti di sistema' : 'Nascondi commenti di sistema'}
                          >
                            <div className={`w-8 h-4 rounded-full transition-colors ${hideSystemComments ? 'bg-gray-300 dark:bg-gray-600' : 'bg-[#0f6cbd]'}`}>
                              <div className={`w-3 h-3 rounded-full bg-white transition-transform transform ${hideSystemComments ? 'translate-x-0.5' : 'translate-x-4'} mt-0.5`} />
                            </div>
                            <span>{hideSystemComments ? 'Sistema nascosto' : 'Sistema visibile'}</span>
                          </button>
                          <button
                            onClick={() => setActiveTab('activity')}
                            className="text-xs text-[#2383E2] dark:text-blue-400 hover:text-[#2383E2]/80 dark:hover:text-blue-200 transition-colors"
                          >
                            Vedi tutti
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {commentiTask.length > 0 && filterComments(commentiTask).length > 0 ? (
                          filterComments(commentiTask).slice(-3).map((comment) => {
                            const ci1 = parseCollaboratorComment(comment.content);
                            const dn1 = ci1.isCollaborator ? ci1.name : (comment.author?.full_name || comment.author?.email);
                            const ai1 = ci1.isCollaborator ? ci1.name.charAt(0).toUpperCase() : (comment.author?.full_name ? comment.author.full_name.charAt(0).toUpperCase() : 'U');
                            return (
                            <div key={comment.id} className={`rounded-lg p-3 border group transition-all ${
                              editingCommentId === comment.id
                                ? 'bg-blue-50 dark:bg-blue-900/10 border-[#2383E2] dark:border-blue-500 shadow-lg ring-2 ring-[#2383E2]/20'
                                : 'bg-[#FBFBFA] dark:bg-gray-800 border-[rgba(55,53,47,0.09)] dark:border-gray-700'
                            }`}>
                              <div className="flex items-start space-x-2">
                                {!ci1.isCollaborator && comment.author?.profile_image_url ? (
                                  <img
                                    src={comment.author.profile_image_url}
                                    alt={comment.author.full_name || comment.author.email || 'User'}
                                    className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${ci1.isCollaborator ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700'}`}>
                                    <span className={`text-xs font-medium ${ci1.isCollaborator ? 'text-amber-700 dark:text-amber-300' : 'text-[#37352F] dark:text-gray-300'}`}>
                                      {ai1}
                                    </span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-xs font-medium text-[#37352F] dark:text-gray-100">
                                        {dn1}
                                      </span>
                                      <span className="text-xs text-[#787774] dark:text-gray-400">
                                        {new Date(comment.created_at).toLocaleDateString('it-IT', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                      {ci1.isCollaborator && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                          Collaboratore
                                        </span>
                                      )}
                                      {(comment.author?.email === 'system' || comment.is_system_comment) && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#787774] dark:text-gray-400">
                                          Sistema
                                        </span>
                                      )}
                                    </div>
                                    {comment.author?.id === user?.id && !comment.is_system_comment && (
                                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={() => handleEditComment(comment)}
                                          className="p-1 text-gray-400 hover:text-blue-500"
                                          title="Modifica commento"
                                        >
                                          <Edit2 className="h-3 w-3" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComment(comment.id)}
                                          className="p-1 text-gray-400 hover:text-red-500"
                                          title="Elimina commento"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  {editingCommentId === comment.id ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2 mb-2 pb-2 border-b border-blue-200 dark:border-blue-800">
                                        <Edit2 className="h-3 w-3 text-[#2383E2]" />
                                        <span className="text-xs font-medium text-[#2383E2]">Modifica in corso</span>
                                      </div>
                                      <CommentEditor
                                        value={editingCommentContent}
                                        onChange={(value, mentions) => {
                                          setEditingCommentContent(value);
                                          setEditingCommentMentions(mentions);
                                        }}
                                        placeholder="Modifica il commento..."
                                        className="block w-full px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-[rgba(55,53,47,0.09)] dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-[#787774] dark:placeholder-gray-500 resize-none"
                                        rows={3}
                                        autoFocus
                                      />
                                      <div className="flex items-center justify-end space-x-2">
                                        <button
                                          onClick={handleCancelEdit}
                                          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                        >
                                          Annulla
                                        </button>
                                        <button
                                          onClick={() => handleUpdateComment(comment.id, comment.content)}
                                          className="px-3 py-1 text-xs bg-[#2383E2] hover:bg-[#2383E2]/80 text-white rounded"
                                        >
                                          Salva
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-[#37352F] dark:text-gray-300">
                                      <ExpandableComment
                                        html={processCommentContent(comment.content)}
                                      />
                                      {ci1.isCollaborator && (
                                        collabTranslations.has(comment.id) ? (
                                          <div className="mt-1 pt-1 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                                            <span className="text-[10px] text-[#787774] dark:text-gray-400 flex items-center gap-1 mb-0.5"><Languages className="w-2.5 h-2.5" />Traduzione:</span>
                                            <span className="text-xs text-[#37352F] dark:text-gray-300">{collabTranslations.get(comment.id)}</span>
                                          </div>
                                        ) : (
                                          <button onClick={() => handleTranslateCollabComment(comment.id, 'en')} disabled={translatingCommentIds.has(comment.id) || !aiEnabled} title={aiEnabled ? undefined : AI_DISABLED_TOOLTIP} className="mt-1 text-[10px] text-[#2383E2] hover:underline flex items-center gap-1 disabled:opacity-50 disabled:no-underline">
                                            {translatingCommentIds.has(comment.id) ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Languages className="w-2.5 h-2.5" />}
                                            Traduci in italiano
                                          </button>
                                        )
                                      )}
                                      {comment.attachments && comment.attachments.length > 0 && (
                                        <CommentAttachments
                                          attachments={comment.attachments}
                                          onDelete={(attachmentId) => deleteCommentAttachment(attachmentId, comment.id)}
                                          disabled={comment.author?.id !== user?.id}
                                        />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            );
                          })
                        ) : (
                          <div className="text-xs text-[#787774] dark:text-gray-400 italic p-3 bg-[#FBFBFA] dark:bg-gray-800 rounded-lg border border-dashed border-[rgba(55,53,47,0.16)] dark:border-gray-600 text-center">
                            Nessun commento ancora. Aggiungi il primo!
                          </div>
                        )}
                      </div>
                      </div>
                    )}
                  </>
                )}

                {/* Activity Tab - hidden in expanded mode */}
                {!isExpanded && activeTab === 'activity' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-[#787774] dark:text-gray-300">
                        Cronologia Attività
                      </label>
                      <button
                            type="button"
                            onClick={() => setHideSystemComments(prev => !prev)}
                            className="flex items-center space-x-2 text-xs text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 transition-colors"
                            title={hideSystemComments ? 'Mostra commenti di sistema' : 'Nascondi commenti di sistema'}
                          >
                            <div className={`w-8 h-4 rounded-full transition-colors ${hideSystemComments ? 'bg-gray-300 dark:bg-gray-600' : 'bg-[#0f6cbd]'}`}>
                              <div className={`w-3 h-3 rounded-full bg-white transition-transform transform ${hideSystemComments ? 'translate-x-0.5' : 'translate-x-4'} mt-0.5`} />
                            </div>
                            <span>{hideSystemComments ? 'Sistema nascosto' : 'Sistema visibile'}</span>
                          </button>
                    </div>
                    <div className="space-y-2">
                      {filterComments(commentiTask).map((comment) => {
                        const ci2 = parseCollaboratorComment(comment.content);
                        const dn2 = ci2.isCollaborator ? ci2.name : (comment.author?.full_name || comment.author?.email);
                        const ai2 = ci2.isCollaborator ? ci2.name.charAt(0).toUpperCase() : null;
                        return (
                        <div key={comment.id} className={`rounded p-2 group transition-all ${
                          editingCommentId === comment.id
                            ? 'bg-blue-50 dark:bg-blue-900/10 border border-[#2383E2] dark:border-blue-500 shadow-lg ring-2 ring-[#2383E2]/20'
                            : 'bg-[#FBFBFA] dark:bg-gray-800'
                        }`}>
                          <div className="flex items-center space-x-2 mb-1">
                            {!ci2.isCollaborator && comment.author?.profile_image_url ? (
                              <img
                                src={comment.author.profile_image_url}
                                alt={comment.author.full_name || comment.author.email || 'User'}
                                className="h-5 w-5 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`h-5 w-5 rounded-full flex items-center justify-center ${ci2.isCollaborator ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700'}`}>
                                {ai2 ? (
                                  <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300">{ai2}</span>
                                ) : (
                                  <User className="h-3 w-3 text-[#787774] dark:text-gray-400" />
                                )}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs font-medium text-[#37352F] dark:text-gray-100">
                                    {dn2}
                                  </span>
                                  <span className="text-xs text-[#787774] dark:text-gray-400">
                                    {new Date(comment.created_at).toLocaleDateString('it-IT')}
                                  </span>
                                  {ci2.isCollaborator && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                      Collaboratore
                                    </span>
                                  )}
                                  {(comment.author?.email === 'system' || comment.is_system_comment) && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#787774] dark:text-gray-200">
                                      Sistema
                                    </span>
                                  )}
                                </div>
                                {comment.author?.id === user?.id && !comment.is_system_comment && (
                                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => handleEditComment(comment)}
                                      className="p-1 text-gray-400 hover:text-blue-500"
                                      title="Modifica commento"
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(comment.id)}
                                      className="p-1 text-gray-400 hover:text-red-500"
                                      title="Elimina commento"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {editingCommentId === comment.id ? (
                            <div className="space-y-2 ml-7">
                              <div className="flex items-center space-x-2 mb-2 pb-2 border-b border-blue-200 dark:border-blue-800">
                                <Edit2 className="h-3 w-3 text-[#2383E2]" />
                                <span className="text-xs font-medium text-[#2383E2]">Modifica in corso</span>
                              </div>
                              <CommentEditor
                                value={editingCommentContent}
                                onChange={(value, mentions) => {
                                  setEditingCommentContent(value);
                                  setEditingCommentMentions(mentions);
                                }}
                                placeholder="Modifica il commento..."
                                className="block w-full px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-[rgba(55,53,47,0.09)] dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-[#787774] dark:placeholder-gray-500 resize-none"
                                rows={3}
                                autoFocus
                              />
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                >
                                  Annulla
                                </button>
                                <button
                                  onClick={() => handleUpdateComment(comment.id, comment.content)}
                                  className="px-3 py-1 text-xs bg-[#2383E2] hover:bg-[#2383E2]/80 text-white rounded"
                                >
                                  Salva
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-[#37352F] dark:text-gray-300 ml-7">
                              <ExpandableComment
                                html={processCommentContent(comment.content)}
                              />
                              {ci2.isCollaborator && (
                                collabTranslations.has(comment.id) ? (
                                  <div className="mt-1 pt-1 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                                    <span className="text-[10px] text-[#787774] dark:text-gray-400 flex items-center gap-1 mb-0.5"><Languages className="w-2.5 h-2.5" />Traduzione:</span>
                                    <span className="text-xs text-[#37352F] dark:text-gray-300">{collabTranslations.get(comment.id)}</span>
                                  </div>
                                ) : (
                                  <button onClick={() => handleTranslateCollabComment(comment.id, 'en')} disabled={translatingCommentIds.has(comment.id) || !aiEnabled} title={aiEnabled ? undefined : AI_DISABLED_TOOLTIP} className="mt-1 text-[10px] text-[#2383E2] hover:underline flex items-center gap-1 disabled:opacity-50 disabled:no-underline">
                                    {translatingCommentIds.has(comment.id) ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Languages className="w-2.5 h-2.5" />}
                                    Traduci in italiano
                                  </button>
                                )
                              )}
                              {comment.attachments && comment.attachments.length > 0 && (
                                <CommentAttachments
                                  attachments={comment.attachments}
                                  onDelete={(attachmentId) => deleteCommentAttachment(attachmentId, comment.id)}
                                  disabled={comment.author?.id !== user?.id}
                                />
                              )}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Files Tab - hidden in expanded mode */}
                {!isExpanded && activeTab === 'files' && (
                  <div>
                    <label className="block text-xs font-medium text-[#787774] dark:text-gray-300 mb-2">
                      File e Allegati
                    </label>
                    {task.attachments && task.attachments.length > 0 ? (
                      <div className="space-y-1">
                        {task.attachments.map((file, index) => (
                          <SignedUrl key={index} url={file.url}>
                          {(href) => (
                          <div
                            className="flex items-center justify-between p-2 bg-[#FBFBFA] dark:bg-gray-800 rounded text-xs cursor-pointer hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50 transition-colors"
                            onClick={() => setPreviewFile({ url: href ?? file.url, type: file.type })}
                          >
                            <div className="flex items-center min-w-0">
                              {getFileIcon(file.type)}
                              <span className="ml-2 text-[#37352F] dark:text-gray-100 truncate">{file.name}</span>
                              <span className="ml-2 text-[#787774] dark:text-gray-400">({formatFileSize(file.size)})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <a
                                href={href ?? undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#787774] hover:text-[#37352F] dark:hover:text-gray-300 transition-colors p-1"
                                onClick={(e) => e.stopPropagation()}
                                download
                              >
                                <Download className="h-3 w-3" />
                              </a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAttachment(file.id, file.name);
                                }}
                                className="text-[#787774] hover:text-red-500 transition-colors p-1"
                                title="Elimina file"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                          )}
                          </SignedUrl>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-[#787774] dark:text-gray-400 italic p-2 bg-[#FBFBFA] dark:bg-gray-800 rounded">
                        Trascina i file qui per caricarli
                      </div>
                    )}

                    {/* Upload Progress */}
                    {Object.entries(uploadingFiles).length > 0 && (
                      <div className="mt-3 space-y-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            Caricamento in corso...
                          </span>
                        </div>
                        {Object.entries(uploadingFiles).map(([fileName, progress]) => (
                          <div key={fileName} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-700 dark:text-gray-300 truncate flex-1 mr-2">{fileName}</span>
                              <span className="text-blue-600 dark:text-blue-400 font-semibold">{Math.round(progress)}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#0f6cbd] transition-all duration-300 rounded-full"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Brief Tab - Project notes and documents */}
                {!isExpanded && activeTab === 'brief' && (
                  <div className="space-y-4">
                    {/* Project Additional Notes - HTML content */}
                    {selectedProject?.metadata?.additional_notes && (
                      <div>
                        <label className="block text-xs font-medium text-[#787774] dark:text-gray-300 mb-2 flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5" />
                          Note Progetto
                        </label>
                        <div
                          className="p-3 bg-[#FBFBFA] dark:bg-gray-800 rounded text-sm text-[#37352F] dark:text-gray-100 prose prose-sm max-w-none dark:prose-invert [&_a]:text-blue-600 [&_a]:hover:underline [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5"
                          dangerouslySetInnerHTML={{ __html: selectedProject.metadata.additional_notes }}
                        />
                      </div>
                    )}

                    {/* Project Documents */}
                    {briefDocuments && briefDocuments.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-[#787774] dark:text-gray-300 mb-2 flex items-center gap-1.5">
                          <FolderOpen className="h-3.5 w-3.5" />
                          Documenti Progetto ({briefDocuments.length})
                        </label>
                        <div className="space-y-1">
                          {briefDocuments.map(doc => {
                            const isLink = doc.document_type === 'link';
                            return (
                              <SignedUrl key={doc.id} url={doc.url}>
                              {(href) => (
                              <a
                                href={href ?? undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-2 bg-[#FBFBFA] dark:bg-gray-800 rounded text-xs cursor-pointer hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50 transition-colors"
                              >
                                <div className="flex items-center min-w-0">
                                  {isLink ? <ExternalLink className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" /> : getFileIcon(doc.type)}
                                  <span className="ml-2 text-[#37352F] dark:text-gray-100 truncate">{doc.name}</span>
                                  {doc.size && !isLink && (
                                    <span className="ml-2 text-[#787774] dark:text-gray-400">({formatFileSize(doc.size)})</span>
                                  )}
                                </div>
                                {isLink ? (
                                  <ExternalLink className="h-3 w-3 text-[#787774] dark:text-gray-400 flex-shrink-0" />
                                ) : (
                                  <Download className="h-3 w-3 text-[#787774] dark:text-gray-400 flex-shrink-0" />
                                )}
                              </a>
                              )}
                              </SignedUrl>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Empty state */}
                    {!selectedProject?.metadata?.additional_notes && (!briefDocuments || briefDocuments.length === 0) && (
                      <div className="text-xs text-[#787774] dark:text-gray-400 italic p-4 bg-[#FBFBFA] dark:bg-gray-800 rounded text-center">
                        Nessuna informazione di brief disponibile per questo progetto
                      </div>
                    )}
                  </div>
                )}

                </div>
            </div>

            {/* Floating Comments Tab for Expanded Mode */}
            {isExpanded && (
              <div className="relative">
                {/* Checklist and Comments Toggle Button */}
                <button
                  onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                  className="absolute bottom-0 right-8 bg-[#2383E2] hover:bg-[#2383E2]/80 text-white px-2 py-1 rounded-t-lg shadow-lg flex items-center space-x-1 transition-all z-20"
                >
                  <MessageSquare className="h-3 w-3" />
                  <span className="text-xs font-medium">
                    Checklist e Commenti
                  </span>
                  {isCommentsOpen ? <ChevronDown className="h-2 w-2" /> : <ChevronUp className="h-2 w-2" />}
                </button>

                {/* Comments Panel Slide-up */}
                <AnimatePresence>
                  {isCommentsOpen && (
                    <motion.div
                      initial={{ y: '100%' }}
                      animate={{ y: 0 }}
                      exit={{ y: '100%' }}
                      transition={{ type: 'tween', duration: 0.3 }}
                      className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t-2 border-[#2383E2] dark:border-blue-400 shadow-2xl max-h-[600px] overflow-y-auto z-10"
                    >
                      <div className="p-6">
                        {/* Checklist Section */}
                        {task.checklist && task.checklist.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-sm font-semibold text-[#37352F] dark:text-gray-100 mb-3">
                              Checklist
                            </h3>
                            <TaskChecklist
                              checklist={task.checklist}
                              taskId={task.id}
                              onToggleItem={toggleChecklistItem}
                              onCreateItem={createTaskChecklistItem}
                              onUpdateItem={updateTaskChecklistItem}
                              onDeleteItem={deleteTaskChecklistItem}
                            />
                          </div>
                        )}

                        {/* Comments Section */}
                        <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 pt-4">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-[#37352F] dark:text-gray-100">
                              Commenti
                            </h3>
                            <button
                            type="button"
                            onClick={() => setHideSystemComments(prev => !prev)}
                            className="flex items-center space-x-2 text-xs text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 transition-colors"
                            title={hideSystemComments ? 'Mostra commenti di sistema' : 'Nascondi commenti di sistema'}
                          >
                            <div className={`w-8 h-4 rounded-full transition-colors ${hideSystemComments ? 'bg-gray-300 dark:bg-gray-600' : 'bg-[#0f6cbd]'}`}>
                              <div className={`w-3 h-3 rounded-full bg-white transition-transform transform ${hideSystemComments ? 'translate-x-0.5' : 'translate-x-4'} mt-0.5`} />
                            </div>
                            <span>{hideSystemComments ? 'Sistema nascosto' : 'Sistema visibile'}</span>
                          </button>
                          </div>
                          <div className="space-y-3">
                          {commentiTask.length > 0 && filterComments(commentiTask).length > 0 ? (
                            filterComments(commentiTask).map((comment) => {
                              const ci3 = parseCollaboratorComment(comment.content);
                              const dn3 = ci3.isCollaborator ? ci3.name : (comment.author?.full_name || comment.author?.email || 'Utente');
                              const ai3 = ci3.isCollaborator ? ci3.name.charAt(0).toUpperCase() : null;
                              return (
                              <div key={comment.id} className={`flex space-x-3 p-3 rounded-lg group transition-all ${
                                editingCommentId === comment.id
                                  ? 'bg-blue-50 dark:bg-blue-900/10 border border-[#2383E2] dark:border-blue-500 shadow-lg ring-2 ring-[#2383E2]/20'
                                  : 'bg-[#FBFBFA] dark:bg-gray-800'
                              }`}>
                                <div className="flex-shrink-0">
                                  {!ci3.isCollaborator && comment.author?.profile_image_url ? (
                                    <img
                                      src={comment.author.profile_image_url}
                                      alt={comment.author.full_name || comment.author.email || 'User'}
                                      className="h-8 w-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${ci3.isCollaborator ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700'}`}>
                                      {ai3 ? (
                                        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{ai3}</span>
                                      ) : (
                                        <User className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-xs font-medium text-[#37352F] dark:text-gray-100">
                                        {dn3}
                                        {ci3.isCollaborator && (
                                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                            Collaboratore
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">
                                        {new Date(comment.created_at).toLocaleString('it-IT')}
                                      </p>
                                    </div>
                                    {comment.author?.id === user?.id && !comment.is_system_comment && (
                                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={() => handleEditComment(comment)}
                                          className="p-1 text-gray-400 hover:text-blue-500"
                                          title="Modifica commento"
                                        >
                                          <Edit2 className="h-3 w-3" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComment(comment.id)}
                                          className="p-1 text-gray-400 hover:text-red-500"
                                          title="Elimina commento"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  {editingCommentId === comment.id ? (
                                    <div className="space-y-2 mt-2">
                                      <div className="flex items-center space-x-2 mb-2 pb-2 border-b border-blue-200 dark:border-blue-800">
                                        <Edit2 className="h-3 w-3 text-[#2383E2]" />
                                        <span className="text-xs font-medium text-[#2383E2]">Modifica in corso</span>
                                      </div>
                                      <CommentEditor
                                        value={editingCommentContent}
                                        onChange={(value, mentions) => {
                                          setEditingCommentContent(value);
                                          setEditingCommentMentions(mentions);
                                        }}
                                        placeholder="Modifica il commento..."
                                        className="block w-full px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-[rgba(55,53,47,0.09)] dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-[#787774] dark:placeholder-gray-500 resize-none"
                                        rows={3}
                                        autoFocus
                                      />
                                      <div className="flex items-center justify-end space-x-2">
                                        <button
                                          onClick={handleCancelEdit}
                                          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                        >
                                          Annulla
                                        </button>
                                        <button
                                          onClick={() => handleUpdateComment(comment.id, comment.content)}
                                          className="px-3 py-1 text-xs bg-[#2383E2] hover:bg-[#2383E2]/80 text-white rounded"
                                        >
                                          Salva
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-sm text-[#37352F] dark:text-gray-300 mt-2">
                                      <ExpandableComment
                                        html={processCommentContent(comment.content)}
                                      />
                                      {ci3.isCollaborator && (
                                        collabTranslations.has(comment.id) ? (
                                          <div className="mt-1 pt-1 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                                            <span className="text-[10px] text-[#787774] dark:text-gray-400 flex items-center gap-1 mb-0.5"><Languages className="w-2.5 h-2.5" />Traduzione:</span>
                                            <span className="text-sm text-[#37352F] dark:text-gray-300">{collabTranslations.get(comment.id)}</span>
                                          </div>
                                        ) : (
                                          <button onClick={() => handleTranslateCollabComment(comment.id, 'en')} disabled={translatingCommentIds.has(comment.id) || !aiEnabled} title={aiEnabled ? undefined : AI_DISABLED_TOOLTIP} className="mt-1 text-[10px] text-[#2383E2] hover:underline flex items-center gap-1 disabled:opacity-50 disabled:no-underline">
                                            {translatingCommentIds.has(comment.id) ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Languages className="w-2.5 h-2.5" />}
                                            Traduci in italiano
                                          </button>
                                        )
                                      )}
                                      {comment.attachments && comment.attachments.length > 0 && (
                                        <CommentAttachments
                                          attachments={comment.attachments}
                                          onDelete={(attachmentId) => deleteCommentAttachment(attachmentId, comment.id)}
                                          disabled={comment.author?.id !== user?.id}
                                        />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              );
                            })
                          ) : (
                            <p className="text-sm text-[#787774] dark:text-gray-400 text-center py-4">
                              Nessun commento
                            </p>
                          )}
                          </div>
                        </div>

                        {/* Comment Form inside slide-up panel */}
                        <div className="mt-4 pt-4 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                          <form onSubmit={handleSubmitComment} className="space-y-3">
                            <CommentEditor
                              value={comment}
                              onChange={(value, mentions) => {
                                setComment(value);
                                setCommentMentions(mentions);
                              }}
                              placeholder="Aggiungi un commento... (digita @ per menzionare qualcuno)"
                              className="block w-full px-0 py-1 text-sm bg-transparent border-none text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder-[#9B9A97] dark:placeholder-gray-500 resize-none"
                              rows={2}
                              onAttachClick={() => commentFileInputRef.current?.click()}
                              attachmentCount={pendingCommentFiles.length}
                            />

                            {/* Pending files preview */}
                            {pendingCommentFiles.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {pendingCommentFiles.map((file, index) => (
                                  <div
                                    key={`pending-exp-${index}`}
                                    className="flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs"
                                  >
                                    {file.type.startsWith('image/') ? (
                                      <Image className="h-3 w-3 text-blue-500" />
                                    ) : (
                                      <FileText className="h-3 w-3 text-gray-500" />
                                    )}
                                    <span className="text-gray-700 dark:text-gray-300 max-w-[100px] truncate">{file.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => setPendingCommentFiles(prev => prev.filter((_, i) => i !== index))}
                                      className="text-gray-400 hover:text-red-500 transition-colors"
                                      title="Rimuovi"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center justify-end">
                              <button
                                type="submit"
                                disabled={(!comment.trim() && pendingCommentFiles.length === 0) || isSubmittingComment}
                                className="px-3 py-1.5 text-xs bg-[#2383E2] hover:bg-[#2383E2]/80 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white disabled:text-gray-500 dark:disabled:text-gray-400 rounded-md transition-colors flex items-center space-x-1"
                              >
                                {isSubmittingComment ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <MessageSquare className="h-3 w-3" />
                                )}
                                <span>Commenta</span>
                              </button>
                            </div>

                            {commentMentions.length > 0 && (
                              <div className="text-xs text-[#2383E2] dark:text-blue-400">
                                {commentMentions.length} {commentMentions.length === 1 ? 'persona' : 'persone'} {commentMentions.length === 1 ? 'verrà notificata' : 'verranno notificate'}
                              </div>
                            )}
                          </form>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Comment Form - hidden in expanded mode */}
            {!isExpanded && (
              <div
                ref={commentSectionRef}
                className="relative border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-4 bg-[#FBFBFA] dark:bg-gray-800/50"
              >
                {/* Comment Zone Overlay - shown when dragging over comment area */}
                {isDragActive && dragOverZone === 'comment' && (
                  <div className="absolute inset-0 bg-green-500 bg-opacity-20 border-4 border-green-500 border-dashed rounded-lg z-50 flex items-center justify-center backdrop-blur-sm pointer-events-none">
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-4 shadow-xl">
                      <Paperclip className="mx-auto h-12 w-12 text-green-500 animate-bounce" />
                      <p className="mt-2 text-sm font-semibold text-green-600 dark:text-green-400">
                        Rilascia per allegare al commento
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmitComment} className="space-y-3">
                  <CommentEditor
                    value={comment}
                    onChange={(value, mentions) => {
                      setComment(value);
                      setCommentMentions(mentions);
                    }}
                    placeholder="Aggiungi un commento... (digita @ per menzionare qualcuno)"
                    className="block w-full px-0 py-1 text-sm bg-transparent border-none text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder-[#9B9A97] dark:placeholder-gray-500 resize-none"
                    rows={2}
                    onAttachClick={() => commentFileInputRef.current?.click()}
                    attachmentCount={pendingCommentFiles.length}
                  />
                  <input
                    ref={commentFileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      handleCommentFileDrop(files);
                      e.target.value = '';
                    }}
                  />

                  {/* Pending files preview */}
                  {pendingCommentFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {pendingCommentFiles.map((file, index) => (
                        <div
                          key={`pending-${index}`}
                          className="flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs"
                        >
                          {file.type.startsWith('image/') ? (
                            <Image className="h-3 w-3 text-blue-500" />
                          ) : (
                            <FileText className="h-3 w-3 text-gray-500" />
                          )}
                          <span className="text-gray-700 dark:text-gray-300 max-w-[100px] truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setPendingCommentFiles(prev => prev.filter((_, i) => i !== index))}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Rimuovi"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bottom row with creation date and comment button */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-[#787774] dark:text-gray-400">
                      {task?.created_at && (
                        <>Creata il {new Date(task.created_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}{task?.created_by_user?.full_name && ` da ${task.created_by_user.full_name}`}</>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={(!comment.trim() && pendingCommentFiles.length === 0) || isSubmittingComment}
                      className="px-3 py-1.5 text-xs bg-[#0f6cbd] hover:bg-[#0a5ba8] disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white disabled:text-gray-500 dark:disabled:text-gray-400 rounded-md transition-colors flex items-center space-x-1"
                    >
                      {isSubmittingComment ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <MessageSquare className="h-3 w-3" />
                      )}
                      <span>Commenta</span>
                    </button>
                  </div>

                  {/* Mention indicator */}
                  {commentMentions.length > 0 && (
                    <div className="text-xs text-[#2383E2] dark:text-blue-400">
                      {commentMentions.length} {commentMentions.length === 1 ? 'persona' : 'persone'} {commentMentions.length === 1 ? 'verrà notificata' : 'verranno notificate'}
                    </div>
                  )}
                </form>
              </div>
            )}

            {/* File Preview Modal */}
            {previewFile && (
              <div className="fixed inset-0 z-[10100] bg-black bg-opacity-75 flex items-center justify-center p-4">
                <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
                  {/* Controls positioned outside the file content */}
                  <div className="absolute -top-12 right-0 flex items-center space-x-3 z-[10101]">
                    <a
                      href={previewFile.url}
                      download
                      className="p-2 text-white hover:text-gray-300 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                      title="Scarica file"
                    >
                      <Download className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => setPreviewFile(null)}
                      className="p-2 text-white hover:text-gray-300 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                      title="Chiudi"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* File content */}
                  {previewFile.type.startsWith('image/') ? (
                    <img
                      src={previewFile.url}
                      alt="Preview"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : (
                    <iframe
                      src={previewFile.url}
                      className="w-full h-full rounded-lg bg-white"
                      title="File preview"
                    />
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </LightModeWrapper>
    {showShareToChat && (
      <ShareToChatModal
        entityType="task"
        entityId={task.id}
        entityData={{
          title: task.title,
          project_name: task.project?.name || '',
          project_id: task.project_id,
          status: task.status,
          priority: task.priority || '',
          assignee: task.assignee?.full_name || '',
        }}
        onClose={() => setShowShareToChat(false)}
      />
    )}

    {/* Calendario visivo (side panel) per schedulare la task sull'assegnatario */}
    <CalendarModal
      isOpen={isScheduleOpen}
      onClose={() => setIsScheduleOpen(false)}
      title="Schedula la task sul calendario"
      size="full"
      mode="sidepanel"
      defaultView="timeGridThreeDay"
      onConfirmSelection={handleScheduleConfirm}
      slotTitle={formState.title || task?.title}
      slotDateTime={scheduleInitialDateTime}
      slotDuration={SCHEDULE_DEFAULT_DURATION}
    />
    </>
  );
}